import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {RequestService} from "../../../services/request/request.service";
import {Observable, Subject, Subscription} from "rxjs";
import {throttleTime} from "rxjs/operators";
import {TableGridListContext} from "./table-grid-list-context";
import {ColumnModel} from "@syncfusion/ej2-grids/src/grid/models/column";
import {parentsUntil} from '@syncfusion/ej2-grids';
import {
  AggregateRowModel,
  AggregateService,
  CellType,
  Column,
  ColumnType,
  ContextMenuItemModel,
  ContextMenuService,
  DataStateChangeEventArgs,
  ExcelExportCompleteArgs,
  ExcelExportProperties,
  ExcelExportService,
  ExcelQueryCellInfoEventArgs,
  FilterService,
  GroupSettingsModel,
  PageService,
  PageSettingsModel,
  PdfExportCompleteArgs,
  PdfExportProperties,
  PdfExportService,
  ResizeService,
  SelectionSettingsModel,
  Sorts,
  SortService,
  SortSettingsModel,
  TextAlign,
  ToolbarItems,
  ToolbarService
} from "@syncfusion/ej2-angular-grids";
import {GridComponent} from "@syncfusion/ej2-angular-grids/src/grid/grid.component";
import {DataManager} from '@syncfusion/ej2-data';
import {TableGridType} from "./table-grid-type.enum";
import {FormDisplayType} from "../../../models/type/form-display-type.enum";
import {ListDataService} from "./list-data.service";
import {ListDataContext} from "./list-data-context";
import {LegacyNavigateService} from "../../../services/legacy-navigate/legacy-navigate.service";
import {ItemModel} from "@syncfusion/ej2-splitbuttons/src/common/common-model";
import {MenuEventArgs} from "@syncfusion/ej2-angular-splitbuttons";
import {UserService} from "../../../services/user/user.service";
import {KnockoutFieldModel} from "../../knockout-field/knockout-field.model";
import {cloneDeep} from 'lodash';
import {ListContentModel} from "../../../models/content/list-content-model";
import {FeatureFlagsService} from "../../../services/feature-flag/feature-flags.service";
import {ContentDataService} from "../../../services/content-data/content-data.service";
import {
  BeforeOpenCloseMenuEventArgs,
  ContextMenuComponent,
  OpenCloseMenuEventArgs
} from "@syncfusion/ej2-angular-navigations";
import {UtilService} from "../../../services/util/util.service";
import {TableOperationService} from "../../../services/table-operation/table-operation.service";
import {ContextMenu} from "../../../services/context-menu/context-menu";
import {ContextMenuActionCallback} from "../../../services/context-menu/context-menu-types";
import {ContextMenuService as SbContextMenuService} from "../../../services/context-menu/context-menu.service";
import {FilterSettingsModel, ResizeSettingsModel} from "@syncfusion/ej2-grids/src/grid/base/grid-model";
import {MagicRendererProperties} from "../../../models/properties/magic-renderer-properties.enum";
import {FormContext} from "../../../models/type/form-context.enum";
import {ClickEventArgs} from '@syncfusion/ej2-navigations';
import {Browser, DateFormatOptions, formatUnit, isNullOrUndefined} from "@syncfusion/ej2-base";
import {ResizeObserver} from "@juggle/resize-observer";
import {ConfigMenuOverride} from "../../../../shared/components/content-page-header/config-menu-override";
import {
  DropDownList,
  ListBoxChangeEventArgs,
  SelectionSettingsModel as ListBoxSelectionSettingsModel,
} from "@syncfusion/ej2-angular-dropdowns";
import {ButtonPropsModel} from "@syncfusion/ej2-popups/src/dialog/dialog-model";
import {DialogComponent, DialogUtility} from "@syncfusion/ej2-angular-popups";
import {ListBoxComponent} from "@syncfusion/ej2-angular-dropdowns/src/list-box/listbox.component";
import {FieldSettingsModel} from "@syncfusion/ej2-lists/src/list-view/list-view-model";
import {MenuItemModel} from "@syncfusion/ej2-navigations/src/common/menu-base-model";
import {SelectEventArgs} from "@syncfusion/ej2-lists/src/list-view/list-view";
import {ListViewComponent} from "@syncfusion/ej2-angular-lists";
import {DatePicker} from "@syncfusion/ej2-angular-calendars";
import {TableGridFilterBarTemplate} from "./table-grid-filter-bar-template";
import {TableMetadataService} from "../../../services/table-metadata/table-metadata.service";
import * as fastDeepEqual from 'fast-deep-equal/es6';
import {InternationalizationService} from "../../../services/internationalization/internationalization.service";
import {$translate, $translateKey} from "../../../services/internationalization/localize.service";
import {MagicFieldProperties} from "../../../models/properties/magic-properties.enum";
import {PdfTrueTypeFont} from '@syncfusion/ej2-pdf-export';
import {LocationService} from "../../../services/location/location.service";
import {NumberFormatOptions} from "@syncfusion/ej2-base/src/internationalization";
import {ListDataAggregateModel} from "./list-data-aggregate.model";
import {AggregateColumnModel} from "@syncfusion/ej2-grids/src/grid/models/models";
import {AggregateType} from "@syncfusion/ej2-grids/src/grid/base/enum";

declare var moment;

declare var $: any;


@Component({
  selector: 'sb-table-grid',
  templateUrl: './table-grid.component.html',
  styleUrls: ['./table-grid.component.scss'],
  encapsulation: ViewEncapsulation.None, // Needed to allow us to override global boostrap4 styles with !important
  providers: [
    PageService,
    SortService,
    ResizeService,
    ContextMenuService,
    ToolbarService,
    ExcelExportService,
    PdfExportService,
    FilterService,
    AggregateService,
    // We need one ListDataService per component otherwise the async pipe in the template means
    // that multiple grids will get the wrong data under certain circumstances.
    ListDataService,
  ]
})
export class TableGridComponent implements OnInit, OnDestroy, AfterViewInit {

  QUERY_EMPTY = '{"and":[]}';
  QUERY_EMPTY_JSON = JSON.parse(this.QUERY_EMPTY);

  NBSP = "&nbsp;"


  // Allows access to the enums from the template.
  TableGridType = TableGridType;
  FormDisplayType = FormDisplayType;

  // Type of the list required - List or RelatedList;
  type: TableGridType;

  // List support /////////////////////////////////

  // For ko integration in tile_loader.js.
  @Input()
  get koTileContentView(): any {
    return this._koTileContentView;
  }
  set koTileContentView(koTileContentView: any) {
    this._koTileContentView = koTileContentView;
    this.type = TableGridType.List;

    // Initialise the Query Builder Configuration with the correct table to remove an unnecessary call
    this.listQueryBuilder = {
      name: 'String',
      datatype: 'knockout-string',
      availableValues: [],
      referenceTable: null,
      readonly: false,
      mandatory: false,
      enablePreview: false,
      tooltip: null,
      data: {
        defaultValue: null, // null | {value?: string, displayValue?: string };
        defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
        answer: {
          value: null,
          displayValue: null
        }, // null | {value?: string, displayValue?: string };
      },
      changed: null,
      requestId: null,
      table: 'SyntheticTable',
      renderer: 'condition',
      rendererProperties: {
        "dependent.table": koTileContentView.table,
        onApplyFilter:  (value) => this.listOnApplyFilter(value),
        "update.model.on.change": true,
        showFooter: true,
        showApply:true,
        showCancel:true,
      },
      valueChangeCallback: (value) => this.listQueryValueChangeCallback(value),
    };
    this.listInstantiateQueryBuilder = true;
  }
  private _koTileContentView: any;

  @ViewChild("listGrid")
  listGrid: GridComponent;

  @ViewChild("listPreviewTemplate")
  public listPreviewTemplate: TemplateRef<{}>;

  @ViewChild("listQueryBuilderContainer")
  listQueryBuilderContainer: ElementRef;

  // used to determine if the component is visible
  @ViewChild("tableGridComponent")
  tableGridComponent: ElementRef;

  // TODO: Page content state - this is a workaround for the fact that we don't have a top level page content component yet.
  contentModel: ListContentModel;

  // Constants

  private LIST_DEFAULT_PAGE_SIZE = 20;
  private SELECTION_COLUMN_WIDTH = 32;
  private PREVIEW_COLUMN_WIDTH = 32;
  private MAX_COLUMN_WIDTH = 400;

  // Tracks whether we have performed the initial load or not for lazy loading
  hasInitialDataLoadStarted: boolean = false;
  hasInitialDataLoadCompleted: boolean = false;

  // List state
  listModel: any;
  listColumns: ColumnModel[] = [];

  listColumnsToIgnore = ['selection', 'preview'];

  listDataSource: Observable<DataStateChangeEventArgs>;

  // Grouping by formatted value does not work with an async data source but does with
  // a data manager, however there are problems with sorting of grouped columns.
  // A ticket has been raised with Syncfusion - 419742
  listDataSource2 = new DataManager();

  // Indicates whether the query builder has loaded/should load, as it will kick off a refresh on initialisation
  hasQueryBuilderLoaded = false;
  // Flag to indicate we can ignore the first 'apply' event that comes from the query builder once it initialises itself
  ignoringFirstQueryBuilderApply = true;

  // Instantiate the query builder lazily so the Requests can be delayed until component visibiliity
  listInstantiateQueryBuilder = false;
  listShowQueryBuilder = false;
  listShowQueryBuilderButton = true;
  listShowContentHeader = true;
  listShowContentFooter = true;
  listShowToolbar = true;
  listCurrentQuery: string = null;
  listTableName: string = null;
  listAspect: string = null;
  listCurrentDataContext: ListDataContext = null;

  listShowPersonalised = true;
  listShowSaved = true;
  listShowExport = true;

  // Indicates the list is coming from the Portal
  listIsPortalComponent = false;

  // The query builder will call the value change callback a couple of times as it is being
  // initialised - we need to ignore them until it has been stabilised.
  listQueryBuilderIgnoreValueChange = true;

  listCurrentSorted: Sorts[] = [];
  listCurrentSkip: number = 0;
  listCurrentTake: number = this.LIST_DEFAULT_PAGE_SIZE;

  listToolbar: ToolbarItems[] | Object = [];

  listDisplaySizesTitle = $translate("ng.component.table-grid.display")`Display ${this.LIST_DEFAULT_PAGE_SIZE}`;
  listRefreshTitle = $translate("ng.component.table-grid.refresh")`Refresh`;
  listSavedFiltersTitle = $translate("ng.component.table-grid.saved-filters")`Saved filters`;
  listFilterTitle = $translate("ng.component.table-grid.filter")`Filter`;
  listExportTitle = $translate("ng.component.table-grid.export")`Export`;
  listClearFilterTitle = $translate("ng.component.table-grid.clear-filter")`Clear filter`;
  listToggleColumnFiltersTitle = $translate("ng.component.table-grid.toggle-column-filters")`Toggle column filters`;
  listToggleGroupingTitle = $translate("ng.component.table-grid.toggle-grouping")`Toggle grouping`;
  listSavedFiltersFilterName = $translate("ng.component.table-grid.saved-filters.filter-name")`Filter name`;
  listSavedFiltersSave = $translate("ng.component.table-grid.saved-filters.save")`Save`;
  listSavedFiltersCancel = $translate("ng.component.table-grid.saved-filters.cancel")`Cancel`;

  listDisplaySizes: ItemModel[] = [
    { text: '10', id: '10' },
    { text: '15', id: '15' },
    { text: '20', id: '20' },
    { text: '25', id: '25' },
    { text: '30', id: '30' },
    { text: '35', id: '35' },
    { text: '40', id: '40' },
    { text: '45', id: '45' },
    { text: '50', id: '50' },
  ];

  @ViewChild('listSelectFilterContextMenu')
  public listSelectFilterContextMenu: ContextMenuComponent;

  listSelectFilterFields: FieldSettingsModel = { text: "text", id: "id" };

  listExportItems: ItemModel[] = [];

  listSelectionSettings: SelectionSettingsModel = {
    type: 'Multiple',
    enableSimpleMultiRowSelection: true,
    checkboxOnly: true,
    persistSelection: true,
  };

  // Context menu for the grid itself. We need at least one entry otherwise you cannot call 'insertAfter' on the menu.
  private LIST_CONTEXT_MENU_HIDDEN_ANCHOR_ID = "-10"; // Don't use -1 as that is a special indicator inside the SF context menu.
  listContextMenuItems: ContextMenuItemModel[] = [
    { text: 'hidden anchor', id: this.LIST_CONTEXT_MENU_HIDDEN_ANCHOR_ID }
  ];
  // Context menu item id for when no bulk record operations available
  private LIST_CONTEXT_MENU_NO_BULK_RECORD_OPERATIONS_ID = "-20"; // Don't use -1 as that is a special indicator inside the SF context menu.

  // Default settings before data is loaded
  listPageSettings: PageSettingsModel = {
    pageSize: this.LIST_DEFAULT_PAGE_SIZE,
    pageCount: 4,
    currentPage: 1,
    totalRecordsCount: 0,
  };

  listResizeSettings: ResizeSettingsModel = {
    mode: 'Normal'
  };

  listSortSettings: SortSettingsModel = {
  };

  // Query builder KO config
  listQueryBuilder: KnockoutFieldModel = null;

  listQueryBuilderViewModel: any;

  listDisplayJson = false;

  listFormContext: FormContext;

  // Related list support /////////////////////////////////

  // For ko integration in related_list_renderer.js
  @Input()
  get koFieldViewModel(): any {
    return this._koFieldViewModel;
  }
  set koFieldViewModel(koFieldViewModel: any) {
    this._koFieldViewModel = koFieldViewModel;
    this.type = TableGridType.RelatedList;
  }
  private _koFieldViewModel: any;

  @Input()
  koFieldViewModeIsReadOnly: boolean = false;

  @Input()
  koFieldRendererProperties: any;

  @Input()
  koFieldDefinition: any;

  @Input()
  koFieldRecord: any;

  @Input()
  koFieldRecordIsNewRecord: boolean = false;

  @Input()
  koFieldFormDisplayType: any;

  @Input()
  koFieldRootViewModel: any;

  @Input()
  koFieldRetainingObservables: any;

  relatedListCurrentIds: any;

  private createButton: HTMLElement;
  private quickEntryButton: HTMLElement;

  // Tracks whether this componenet has been destroyed because some
  // Events are sticking around for some reason (Knockout integration)
  private isDestroyed: boolean = false;

  // Custom Related list support /////////////////////////////////
  // For ko integration in ko.bindingHandlers.customRelatedList in bindings.js

  @Input()
  get customRelatedListId(): any {
    return this._customRelatedListId;
  }
  set customRelatedListId(customRelatedListId: any) {
    this._customRelatedListId = customRelatedListId;
    this.type = TableGridType.CustomRelatedList;
  }
  private _customRelatedListId: any;

  @Input()
  private customRelatedListParentId: any;

  @Input()
  private customRelatedListParentTable: any;

  @Input()
  private customRelatedListParentRecord: any;

  private customRelatedList: any;
  private customRelatedListTable: any;
  private customRelatedListQuery: any;

  // Embedded list support  ///////////////////////////////////////

  // Hacky way to call a function
  @Input()
  get embeddedListRefresh(): string {
    return this._embeddedListRefresh;
  }
  set embeddedListRefresh(_: string) {
    this._embeddedListRefresh = _;
    this.refresh();
    this.clearSelection();
  }
  private _embeddedListRefresh: any;

  @Input()
  get updateRelatedListIds(): string {
    return this._updateRelatedListIds;
  }
  set updateRelatedListIds(_: string) {
    this._updateRelatedListIds = _;
    // this.koFieldRetainingObservables.koFieldViewModel.value() contains the current set of values which haven't been sent to the server yet.
    this.relatedListCurrentIds = this.koFieldRetainingObservables.koFieldViewModel.value().map((v) => {
      return (typeof v.id === "function" ? v.id() : v.id);
    });
  }
  private _updateRelatedListIds: any;

  // Selected records support ///////////////////////////////////////

  // Return a thunk that returns the selected records
  @Input()
  get getSelectedRecords(): () => any[] {
    const self = this;
    return () => {
      return self._getSelectedRecords();
    };
  }

  // Common support ///////////////////////////////////////

  // Variable to optimise display of the list page - to not display until the initial data is loaded to reduce
  // flicker.
  initialDataVisibility: any = { visibility: 'hidden' };

  // Custom config menu support
  listConfigMenuOverrides: ConfigMenuOverride = {
    "angular:PersonaliseList": (_: any) => {
      this.listCustomFieldsFilterShowDialog();
    },
    "angular:UnpersonaliseList": (_: any) => {
      this.deleteCustomListDefinition();
    },
    "angular:PersonaliseListSorting": (_: any) => {
      this.listCustomSortingShowDialog(false);
    },
    "angular:UnpersonaliseListSorting": (_: any) => {
      this.deleteCustomListSorting(false);
    },
    "angular:PersonaliseListColumnWidths": (_: any) => {
      this.saveCustomListColumnWidths();
    },
    "angular:UnpersonaliseListColumnWidths": (_: any) => {
      this.deleteCustomListColumnWidths();
    },
    "angular:DefaultListSorting": (_: any) => {
      this.listCustomSortingShowDialog(true);
    },
    "angular:RemoveDefaultListSorting": (_: any) => {
      this.deleteCustomListSorting(true);
    },
  };

  // Custom list definition support

  listCustomFields: string[] = [];

  listCustomFieldsExtraFields = { iconCss: "iconCss" };

  // Fields that should appear in the field filter
  listCustomFieldsIncludedFields = [];

  // Fields that should not appear in the field filter
  listCustomFieldsAvailableFields = [];

  listCustomFieldsVisible = false;

  listCustomFieldsPosition: any = { X: 'center', Y: 'center' };

  listCustomFieldsButtons: ButtonPropsModel[] = [{}];

  listCustomFieldsContext: any = {};

  listCustomFieldsTitle: string;

  listCustomFieldsTitleLHS: string = "ng.component.table-grid.available-columns";

  listCustomFieldsTitleRHS: string = "ng.component.table-grid.included-columns";

  listCustomFieldsFilterTitle: string = "ng.component.table-grid.select-columns";

  public listCustomFieldsFilterButtons: ButtonPropsModel[] = [
    {
      'click': this.listCustomFieldsFilterHideDialog.bind(this, false),
      buttonModel: {
        content: $translateKey('common.button.cancel'),
      }
    },
    {
      'click': this.listCustomFieldsFilterHideDialog.bind(this, true),
      buttonModel: {
        content: $translateKey('common.button.save'),
        isPrimary: true,
      }
    },
  ];

  listCustomFieldsExportTitle: string = "ng.component.table-grid.select-export-columns";

  public listCustomFieldsExportButtons: ButtonPropsModel[] = [
    {
      'click': this.listCustomFieldsExportHideDialog.bind(this, false),
      buttonModel: {
        content: $translateKey('common.button.cancel'),
      }
    },
    {
      'click': this.listCustomFieldsExportHideDialog.bind(this, true),
      buttonModel: {
        content: $translateKey('common.button.export'),
        isPrimary: true,
      }
    },
  ];

  @ViewChild('ejFieldChooserDialog', {static: false}) ejDialog: DialogComponent;

  @ViewChild('ejAvailableFields', {static: false}) ejAvailableFields: ListBoxComponent;

  @ViewChild('ejIncludedFields', {static: false}) ejIncludedFields: ListBoxComponent;

  // Custom list sorting support

  listCustomSorting: string = "";
  listCustomSortingUser: boolean = false;

  // Fields that should appear in the field sorting filter
  public listCustomSortingIncludedFields = [];

  // Fields that should not appear in the field sorting filter
  public listCustomSortingAvailableFields = [];

  public listUserCustomSortingButtons: ButtonPropsModel[] = [
    {
      'click': this.listCustomSortingHideDialog.bind(this, false, false),
      buttonModel: {
        content: $translateKey('common.button.cancel'),
      }
    },
    {
      'click': this.listCustomSortingHideDialog.bind(this, true, false),
      buttonModel: {
        content: $translateKey('common.button.save'),
        isPrimary: true,
      }
    },
  ];

  public listDefaultCustomSortingButtons: ButtonPropsModel[] = [
    {
      'click': this.listCustomSortingHideDialog.bind(this, false, true),
      buttonModel: {
        content: $translateKey('common.button.cancel'),
      }
    },
    {
      'click': this.listCustomSortingHideDialog.bind(this, true, true),
      buttonModel: {
        content: $translateKey('common.button.save'),
        isPrimary: true,
      }
    },
  ];

  public listCustomSortingButtons = this.listUserCustomSortingButtons;

  listCustomSortingExtraFields = { iconCss: "iconCss" };

  public listCustomSortingVisible = false;

  public listCustomSortingPosition: any = { X: 'center', Y: 'center' };

  listCustomSortingTitle: string = "ng.component.table-grid.select-sorting-columns";

  listCustomSortingSelectionSettings: ListBoxSelectionSettingsModel = {
    mode: 'Single'
  };

  @ViewChild('ejFieldSortingDialog', {static: false}) ejSortingDialog: DialogComponent;

  @ViewChild('ejAvailableSortingFields', {static: false}) ejAvailableSortingFields: ListBoxComponent;

  @ViewChild('ejIncludedSortingFields', {static: false}) ejIncludedSortingFields: ListBoxComponent;

  // Custom list column widths support

  listCustomColumnWidths: any = null;

  // private state

  // A Resize Observer that allows us to see when Tabs etc are switched to and components become visible
  private resizeObserver: ResizeObserver;

  // Create an RxJS Subject to throttle the resizeObserver, as it can generate many events
  private resizeSubject$ = new Subject<ResizeObserverEntry[]>();

  @ViewChild("listWrapper", {static: false})
  private listWrapper: ElementRef;

  private contentModelSubscription: Subscription;

  private uploadAttachmentSubscription: any;
  private updateRecordSubscription: any;
  private refreshListSubscription: any;
  private listMessageBusRefreshSubscription: any;

  private localeChanged$: Subscription;

  constructor(private hostElement: ElementRef<HTMLElement>,
              private requestService: RequestService,
              private i18nService: InternationalizationService,
              private navigateService: LegacyNavigateService,
              private userService: UserService,
              private utilService: UtilService,
              public listDataService: ListDataService,
              private contentDataService: ContentDataService,
              private featureFlagsService: FeatureFlagsService,
              private contextMenuService: SbContextMenuService,
              private tableOperationService: TableOperationService,
              private tableMetadataService: TableMetadataService,
              private locationService: LocationService,
              private zone: NgZone) {
    this.listDataSource = listDataService;
  }

  ngOnInit(): void {
    this.listDisplayJson = this.featureFlagsService.isOn("syncfusion-table-grid-display");

    // Identify where we are in the UI - note that this may sometimes not be known - e.g. a filtered list on the portal page or dashboard.
    this.listFormContext = this.hostElement.nativeElement.closest("[data-context]")?.attributes['data-context']?.value || FormContext.UNKNOWN;

    // Resize Observer support. Just chain to the RxJS Subject so we can throttle
    this.resizeObserver = new ResizeObserver((entries, observer) => this.resizeSubject$.next(entries));

    // Apply throttling to the ResizeObserver events
    this.resizeSubject$.pipe(
      throttleTime(100)
    ).subscribe((entries) => {
      this.zone.run(() => {
        // Always exit if we aren't visible on the page - as no reason to do anything
        if (!this.isVisibleOnPage()) return;

        let debug = false;

        if (debug) console.info(`!!resizeObserver [isVisibleOnPage=${this.isVisibleOnPage()}] [hasInitialDataLoadCompleted=${this.hasInitialDataLoadCompleted}] [listInstantiateQueryBuilder=${this.listInstantiateQueryBuilder}] [hasQueryBuilderLoaded=${this.hasQueryBuilderLoaded}]`)
        if (debug) console.info(`!!resizeObserver [listFormContext=${this.listFormContext}] [listIsPortalComponent=${this.listIsPortalComponent}] [listTableName=${this.listTableName}] [type=${this.type}]`)

        // If everything has already loaded, we just want to resize fields
        if (this.hasInitialDataLoadCompleted) {
          if (debug) console.info("resizeObserver: resize entries only");
          entries.forEach((e) => {
            if (e.target === this.listWrapper.nativeElement) {
              this.resizeColumns(false);
            }
          });
          return;
        }

        // This would be a fringe case, if the load had started but a slow request meant we ended up here
        if (this.hasInitialDataLoadStarted) {
          if (debug) console.info("resizeObserver: hasInitialDataLoadStarted");
          return;
        }

        // A standard list on a standalone list page, or a list on a Portal Component (Report etc.)
        if (this.type == TableGridType.List) {
          if (debug) console.info("resizeObserver: Standard table list load started");

          // If the query builder will fire and force the refresh, just initiate that process
          if (this.listInstantiateQueryBuilder && !this.hasQueryBuilderLoaded && this.listShowQueryBuilder) {
            this.hasQueryBuilderLoaded = true;
          } else {
            // Otherwise load the data
            this.loadInitialData();
          }
          // We are done
          return;
        }

        // For Related Lists - just perform the load
        if (this.type == TableGridType.RelatedList || this.type == TableGridType.CustomRelatedList) {
          if (debug) console.info("resizeObserver: Standard related list load started");
          this.loadInitialData();
        }
      });
    });

    this.allowFiltering = true;

    if (this.type != TableGridType.List) {
      this.listShowPersonalised = false;
      this.listShowSaved = false;
      this.listShowExport = false;
    }

    if (this?.koFieldRecordIsNewRecord === true || this.type !== TableGridType.List) {
      this.listShowToolbar = false;
    }

    if (this.type == TableGridType.List) {
      this.listMessageBusRefreshSubscription = (window as any).MessageBus.subscribe(
        (window as any).MessageBus.CHANNELS.REFRESH_EVENT,
        "*",
        () => this.refresh()
      );
    }

    this.localeChanged$ = this.i18nService.localeChanged$.subscribe((locale: string) => {
      this.loadInitialData();
      this.initExportMenu();
    });
  }

  ngAfterViewInit(): void {

    // We need this to occur in a timeout to give the form time to display the Tabs
    setTimeout(() => {
      // Only perform the initial data load when the list becomes visible
      if (!this.listInstantiateQueryBuilder && this.isVisibleOnPage()) {
        this.loadInitialData();
      }
    });

    this.initExportMenu();
    this.resizeObserver.observe(this.listWrapper.nativeElement);
  }

  ngOnDestroy(): void {
    // Mark as destroyed. Necessary because there are some elements that are registered
    // with Knockout/Syncfusion that make callbacks after the item should be destroyed.
    // This should be fixed, but until then we need to prevent these from triggering
    this.isDestroyed = true;

    this.resizeObserver.unobserve(this.listWrapper.nativeElement);

    this.contentModelSubscription && this.contentModelSubscription.unsubscribe();
    this.uploadAttachmentSubscription && this.uploadAttachmentSubscription.unsubscribe();
    this.updateRecordSubscription && this.updateRecordSubscription.unsubscribe();
    this.refreshListSubscription && this.refreshListSubscription.unsubscribe();

    this.localeChanged$ && this.localeChanged$.unsubscribe();

    // Remove the create/quick entry buttons, as they setup listeners that do bad things on callback if they are no longer in context
    if (this.createButton) {
      $(this.createButton).remove();
      this.createButton = null;
    }
    if (this.quickEntryButton) {
      $(this.quickEntryButton).remove();
      this.quickEntryButton = null;
    }

    if (this.listMessageBusRefreshSubscription) {
      (window as any).MessageBus.unsubscribe(this.listMessageBusRefreshSubscription)
    }
  }


  /**
   * Checks if the component is currently visible on the page.
   *
   * @returns {boolean} True if the component is currently visible on the page, otherwise false.
   */
  isVisibleOnPage(): boolean {
    let isVisible = this.tableGridComponent.nativeElement.offsetParent !== null;
    return isVisible;
  }

  // Cannot manually refresh the grid unless all the modules exist, but there is no obvious way
  // to tell via the API. 'headerModule' is the easiest one to check.
  refresh() {
    // console.info("Refresh");

    if (!this.hasInitialDataLoadCompleted && this.hasQueryBuilderLoaded) {
      // If we get a refresh event and we haven't yet loaded the data, do so
      this.loadInitialData()
    } else if (this.hasInitialDataLoadCompleted && this.listGrid?.headerModule) {
      // The list has been initialised, so just request a refresh
      this.listGrid?.refresh();
    }
  }

  // Cannot manually clear the selection on the grid unless all the modules exist, but there is no obvious way
  // to tell via the API. 'headerModule' is the easiest one to check.
  clearSelection() {
    if (this.listGrid?.headerModule) {
      this.listGrid?.clearSelection();
    }
  }

  // selected records support

  private _getSelectedRecords(): any[] {
    const selectedRecordIds = this.listGrid.getSelectedRecords().map((record: any) => record._dataFn().id);

    const selectedRecords = this.listModel.data.filter((d) => selectedRecordIds.includes(d.id));

    return selectedRecords;
  }

  // Value is serialised query
  listQueryValueChangeCallback(value: string) {
    if (this.listQueryBuilderIgnoreValueChange) {
      console.debug('TableGridComponent.listQueryValueChangeCallback - ignoring value change as not initialised yet', value);
    } else {
      this.listCurrentQuery = value;
    }
  }

  // Value is json query
  listOnApplyFilter(value: any) {
    if (this.ignoringFirstQueryBuilderApply) {
      this.ignoringFirstQueryBuilderApply = false;
      return;
    }
    this.listCurrentQuery = JSON.stringify(value);
    this.listQueryBuilder.data.answer.value = this.listCurrentQuery;
    this.listQueryBuilder.data.answer.displayValue = this.listCurrentQuery;

    this.refresh();
  }

  private getDefaultFormattedCellValue(column: Column, data: any): any {
    const cellRendererFactory = this.listGrid.serviceLocator.getService("cellRendererFactory");
    return (cellRendererFactory as any).getCellRenderer(CellType.Data).format(column, data[column.field], data);
  }

  private getSubstituteFormattedCellValue(column: Column, data: any, substitute: any): any {
    const cellRendererFactory = this.listGrid.serviceLocator.getService("cellRendererFactory");
    return (cellRendererFactory as any).getCellRenderer(CellType.Data).format(column, substitute, data);
  }

  private getFormattedCellValue(column: Column, data: any): any {
    const _data = data._dataFn();
    const fieldType = _data[column.field]?.fieldType;
    let displayValue: string;
    switch (fieldType) {
        case "DURATION":
            displayValue = _data[column.field]?.displayValue;
            return this.getSubstituteFormattedCellValue(column, data, displayValue);
        case "PASSWORD":
            displayValue = "*******";
            return this.getSubstituteFormattedCellValue(column, data, displayValue);
        case "INTEGER":
        case "DECIMAL": {
            const ldElement = _data.listModel.tableDefinition._fields.find((f) => f._fieldName === column.field);
            const vdElement = _data.listModel.viewDefinition.containers[0].elements.find((e) => e.type === 'TABLE_FIELD' && e.fieldName === column.field);
            const renderer1 = ldElement?._renderer;
            const properties = ldElement?._properties || {};
            const renderer2 = vdElement?.renderer;
            const rendererProperties = vdElement?.rendererProperties || {};
            let result = this.getDefaultFormattedCellValue(column, data);
            if (renderer1 === 'accounting' || renderer2 === 'accounting') {
                let value = _data[column.field]?.value;
                value = value && !isNaN(value) ? value : 0;

                let fractionDigits = properties[MagicRendererProperties.NUMBER_FORMAT_FRACTION_DIGITS] || rendererProperties[MagicRendererProperties.NUMBER_FORMAT_FRACTION_DIGITS];
                fractionDigits = fractionDigits != null ? fractionDigits : 2;

                const currency = properties[MagicRendererProperties.ACCOUNTING_CURRENCY] || rendererProperties[MagicRendererProperties.ACCOUNTING_CURRENCY];
                const currencyCode = properties[MagicRendererProperties.ACCOUNTING_CURRENCY_CODE] || rendererProperties[MagicRendererProperties.ACCOUNTING_CURRENCY_CODE];
                const numberBuilder = this.i18nService.numberBuilder().withFractionDigits(fractionDigits);

                if (currencyCode) {
                    numberBuilder.currency(currencyCode).asCurrency();
                    result = this.i18nService.formatNumber(value, numberBuilder.build());
                } else {
                    result = this.i18nService.formatNumber(value, numberBuilder.build());
                    result = (currency != null ? currency : "") + value;
                }
            } else if (renderer1 == "percentage" || renderer2 == "percentage") {
                let value = _data[column.field]?.value;
                value = value && !isNaN(value) ? value : 0;

                let fractionDigits = properties[MagicRendererProperties.NUMBER_FORMAT_FRACTION_DIGITS] || rendererProperties[MagicRendererProperties.NUMBER_FORMAT_FRACTION_DIGITS];
                fractionDigits = fractionDigits != null ? fractionDigits : 2;

                const numberBuilder = this.i18nService.numberBuilder().withFractionDigits(fractionDigits).asPercentage();
                result = this.i18nService.formatNumber(value, numberBuilder.build());
            }
            return result;
      }
      case "MONEY": {
        const numberBuilder = this.getMoneyNumberBuilder(_data.listModel, column.field);

        let value = data[column.field]
        return this.i18nService.formatNumber(value, numberBuilder.build());
      }
      case "PERCENTAGE": {
          const vdElement = _data.listModel.viewDefinition.containers[0].elements.find((e) => e.type === 'TABLE_FIELD' && e.fieldName === column.field);
          const ldElement = _data.listModel.tableDefinition._fields.find((f) => f._fieldName === column.field);
          const properties = ldElement?._properties || {};
          const rendererProperties = vdElement?.rendererProperties || {};

          let value = _data[column.field]?.value;
          value = value && !isNaN(value) ? value : 0;

          let fractionDigits = properties[MagicRendererProperties.NUMBER_FORMAT_FRACTION_DIGITS] || rendererProperties[MagicRendererProperties.NUMBER_FORMAT_FRACTION_DIGITS];
          fractionDigits = fractionDigits != null ? fractionDigits : 2;

          const numberBuilder = this.i18nService.numberBuilder().maximumFractionDigits(fractionDigits).asPercentage();
          // /100 to compensate the multiplication from sync fusion percentage format
          return this.i18nService.formatNumber(value / 100, numberBuilder.build());
      }
      case "DATE_TIME":
        const substitute = this.i18nService.toLocaleDateOfTimezone(data[column.field], this.userService.getTimeZone())
        return this.getSubstituteFormattedCellValue(column, data, substitute);
      default:
        return this.getDefaultFormattedCellValue(column, data);
    }
  }

  private getMoneyNumberBuilder(listModel, fieldName: string) {
    const ldElement = listModel.tableDefinition._fields.find((f) => f._fieldName === fieldName);
    const vdElement = listModel.viewDefinition.containers[0].elements.find((e) => e.type === 'TABLE_FIELD' && e.fieldName === fieldName);
    const fieldDefProperties = ldElement?._properties || {};
    const fieldDefRenderProperties = ldElement._rendererProperties || {};
    const rendererProperties = vdElement?.rendererProperties || {};
    let fractionDigits = rendererProperties[MagicRendererProperties.NUMBER_FORMAT_FRACTION_DIGITS] || fieldDefProperties[MagicRendererProperties.NUMBER_FORMAT_FRACTION_DIGITS];
    const currencySymbol = rendererProperties[MagicRendererProperties.MONEY_CURRENCY_SYMBOL] || fieldDefRenderProperties[MagicRendererProperties.MONEY_CURRENCY_SYMBOL];
    const currencyCode = fieldDefProperties[MagicFieldProperties.MONEY_CURRENCY_CODE];

    const numberBuilder = this.i18nService.numberBuilder()
      .currency(currencyCode)
      .asAccounting();

    if (fractionDigits) {
      numberBuilder.withFractionDigits(parseInt(fractionDigits));
    }

    let enforceCurrencySymbol = currencySymbol === null || currencySymbol === undefined ?  this.userService.getEnforceCurrencySymbol()
      : currencySymbol;

    if (enforceCurrencySymbol) {
      numberBuilder.altSymbol('true');
    }
    return numberBuilder;
  }

// Initialise the list and bring back the first set of data

  async loadInitialData() {
    let result: TableGridListContext;
    let table: string;
    let aspect: string;
    let query: any;
    let method: string;
    let tile: string;
    let tileID: string;
    let firstResult: number;
    let maxResults: number;
    let sortOrder: any[];

    // Mark as started
    this.hasInitialDataLoadStarted = true;

    // Don't even try to load this up in form builder.
    if (this.koFieldFormDisplayType === FormDisplayType.FORM_BUILDER) {
      return;
    }

    if (this.type == TableGridType.List) {

      const storedPageSize = this.loadIntegerUserProperty("list.pagination.size", this.LIST_DEFAULT_PAGE_SIZE);

      // This is a tile request to get a list, taking into account the URL query params.
      // It might also be a filtered list on a dashboard or portal page.

      // An object with all the url search parameters. This allows us to set the default customFields and sort order
      // from the url, e.g. from a navigation menu item.
      const urlSearchParameters = this._koTileContentView.urlSearchParameters;

      table = this._koTileContentView.table;
      aspect = this._koTileContentView.aspect;
      tileID = this._koTileContentView.tileID;
      tile = this._koTileContentView.tile;
      method = this._koTileContentView.method;
      sortOrder = [];

      query = this._koTileContentView.query;
      firstResult = this._koTileContentView.firstResult || 0;
      maxResults = this._koTileContentView.maxResults || storedPageSize || this.LIST_DEFAULT_PAGE_SIZE;

      this.listDisplaySizesTitle = $translate("ng.component.table-grid.display")`Display ${maxResults}`;
      this.listRefreshTitle = $translate("ng.component.table-grid.refresh")`Refresh`;
      this.listSavedFiltersTitle = $translate("ng.component.table-grid.saved-filters")`Saved filters`;
      this.listExportTitle = $translate("ng.component.table-grid.export")`Export`;
      this.listClearFilterTitle = $translate("ng.component.table-grid.clear-filter")`Clear filter`;
      this.listToggleColumnFiltersTitle = $translate("ng.component.table-grid.toggle-column-filters")`Toggle column filters`;
      this.listToggleGroupingTitle = $translate("ng.component.table-grid.toggle-grouping")`Toggle grouping`;
      this.listSavedFiltersFilterName = $translate("ng.component.table-grid.saved-filters.filter-name")`Filter name`;
      this.listSavedFiltersSave = $translate("ng.component.table-grid.saved-filters.save")`Save`;
      this.listSavedFiltersCancel = $translate("ng.component.table-grid.saved-filters.cancel")`Cancel`;

      // Assign the correct values for table and aspect and query. The URL cannot be used as it can be interpreted
      // in many ways.
      this.listTableName = table;
      this.listAspect = aspect;
      this.listCurrentQuery = query && query.getQuery() ? JSON.stringify(query.getQuery()) : null;

      const rendererPropertiesEmbeddedList = this.koTileContentView?.parentViewModel?.elements?.[0]?.elements?.[0]?.rendererProperties;

      // This can happen if the filtered list is on a dashboard or portal page, or a report drill down
      if (rendererPropertiesEmbeddedList &&
        (rendererPropertiesEmbeddedList["embeddedList"] || rendererPropertiesEmbeddedList["tile"] === "ReportDrillDown")) {
        // Reassign the source table to be the one from the tile content view rather than the one from the URL.
        this.listTableName = rendererPropertiesEmbeddedList["table"];

        // Reassign the current query to be the one from the renderer properties set up by the embedded list..
        this.listCurrentQuery = rendererPropertiesEmbeddedList["query"];

        // Reassign the columns and sort order based on the renderer properties, if they are set
        const possibleColumns = rendererPropertiesEmbeddedList['columns'];
        const possibleSortOrder = rendererPropertiesEmbeddedList['sortOrder'];
        if (this.listCustomFields.length == 0 && possibleColumns) {
          this.listCustomFields = possibleColumns.split(",");
        }
        if (this.listCustomSorting == "" && possibleSortOrder) {
          this.listCustomSorting = possibleSortOrder;
        }

        // TODO: does the aspect change as well?

        // There are many more renderer properties that could be handled but they don't appear to be
        // used in the current list either. From bindings.js:
        //   "showQueryBuilder",
        //   "showTileHeader",
        //   "showTileFooter",
        //   "showTableHeader",
        //   "showTablePagination",
        //   "showDisplayLength",
        //   "suppressRefreshButton",
        //   "suppressStyleTileContent",
        //   "suppressStyleDataTable",
        //   "suppressStyleGridFading",
        //   "suppressStyleTableFooter",
        //   "suppressRowPreview",
        //   "suppressCheckboxes",
        //   "showTableOperations",
        //   "showListContextOperations",
        //   "previewWithDefaultAspect"

        this.listShowQueryBuilderButton = rendererPropertiesEmbeddedList["showQueryBuilder"] === "true";
        this.listInstantiateQueryBuilder = rendererPropertiesEmbeddedList["showQueryBuilder"] === "true";
        this.listShowContentHeader = rendererPropertiesEmbeddedList["showTileHeader"] === "true";
        this.listShowContentFooter = rendererPropertiesEmbeddedList["showTileFooter"] === "true";
        this.listShowToolbar = rendererPropertiesEmbeddedList["showListToolbar"] === "true";

        // Magic property to indicate the list is coming from the Portal
        this.listIsPortalComponent = rendererPropertiesEmbeddedList["isPortalComponent"] === "true";
      }

      // Custom list definitions from embedded lists, except maybe for ReportDrillDown.
      if (!(rendererPropertiesEmbeddedList && rendererPropertiesEmbeddedList["embeddedList"])) {
        await this.loadCustomListDefinition();
        await this.loadCustomListSorting();
        await this.loadCustomListColumnWidths();
      }

      // We set the initial columns and sort order if supplied in the url and not set by any saved settings
      const possibleColumns = urlSearchParameters['columns'];
      const possibleSortOrder = urlSearchParameters['sortOrder'];
      if (this.listCustomFields.length == 0 && possibleColumns) {
        this.listCustomFields = possibleColumns.split(",");
      }
      if (this.listCustomSorting == "" && possibleSortOrder) {
        this.listCustomSorting = possibleSortOrder;
      }

      this.listCurrentSorted = this.listDataService.convertToDataGridSorted(this.listCustomSorting);
      sortOrder = this.listDataService.convertToControllerQuerySorted(this.listCurrentSorted);

      const controllerQuery = this.requestService.createControllerQuery();

      controllerQuery.firstResult(firstResult);
      controllerQuery.maxResults(maxResults);
      controllerQuery.sortOrder(sortOrder);

      if (this.listCurrentQuery) {
        controllerQuery.query(JSON.parse(this.listCurrentQuery));
      }

      const readFromParentData = this.koTileContentView?.parentViewModel?.rendererProperties?.readFromParentData;

      const data = {
        ...{ customFields: this.listCustomFields },
        ...(readFromParentData ?? {})
    };

      this.listCurrentDataContext = {
        type: this.type,
        table: table,
        tileID: tileID,
        tile: tile,
        method: method,
        data: data,
        readFromParentData: readFromParentData
      };

      try {
        result = await this.requestService.createRequest()
          .table(table)
          .tile(tile)
          .tileID(tileID)
          .method(method)
          .aspect(aspect)
          .controllerQuery(controllerQuery)
          .data(data)
          .execute() as TableGridListContext;
      } catch (ex) {
        console.error("TableGridComponent.loadInitialData", this.patchListDataContext(this.listCurrentDataContext), ex);
      }

      // If we are not on page 1 initially we need to move the pager, and possibly change the page size
      // currentPage is 1-based
      if (firstResult !== 0) {
        const currentPage = Math.floor(firstResult / maxResults) + 1;
        this.listGrid.pagerModule.pagerObj.currentPage = currentPage;
        if (this.listGrid.pageSettings.pageSize !== maxResults) {
          this.listGrid.pagerModule.pagerObj.pageSize = maxResults;
        }
        this.listCurrentTake = this.listGrid.pagerModule.pagerObj.pageSize;
        this.listCurrentSkip = (this.listGrid.pagerModule.pagerObj.currentPage - 1) *  this.listCurrentTake;
      }

      const savedFilters = this.loadUserProperty(this.getSavedFiltersListDefinitionKey(), "[]");
      this.savedFiltersListViewData = savedFilters ? JSON.parse(savedFilters) : [];

      // Patch the ids in case there are old ones that cause UI errors (containing a comma, for example)
      this.savedFiltersListViewData.forEach(f => {
          if (!this.utilService.validateCSSID(f.id)) {
            f.id = this.utilService.generateCSSID();
          }
      });

      this.savedFiltersListViewData.push({
        text:  $translate("ng.component.table-grid.saved-filters.new")`New`,
        id: 'new'
      });

      // Put in a message bus subscription for refreshes from Navigate.refresh()
      this.refreshListSubscription = (window as any).MessageBus.subscribe(
        (window as any).MessageBus.CHANNELS.REFRESH_EVENT,
        "TENANCY_FILTER_CHANGE",
        (data: any, envelope: any) => {
          this.refresh();
        });

    }  else if (this.type === TableGridType.RelatedList) {
      // This is a SystemController list request to get a related list on a detail form.
      table = this._koFieldViewModel._data._tableClass;
      tileID = null;
      tile = "ListAutoScaffold";
      method = "list";
      firstResult = 0;
      maxResults = 10;
      sortOrder = [];
      aspect = this.koFieldRootViewModel.getAspect();

      const sourceTableId = this.koFieldRecord.id;
      const relationName = this._koFieldViewModel._data._referenceName;
      const sourceTable = this._koFieldViewModel.getTableName();
      const targetTable = this._koFieldViewModel.getTargetTableName();
      const targetRelationName = this.koFieldDefinition.fieldName;
      const targetRelation = (window as any).TableManager.getTableDefinition(table).getRelation(targetRelationName);

      this.listTableName = targetTable;
      this.listAspect = aspect;

      // PF - ParentRecord is used when the related list is displaying all the attachments for a record.
      let reverseRelation = targetRelation.getReverseRelationName();

      if (targetTable === "Attachment" && reverseRelation == null) {
        reverseRelation = "ParentRecord";
      }

      // Put in a message bus subscription to know when uploads are happening so we can refresh the list.
      if (targetTable === "Attachment") {
        this.uploadAttachmentSubscription = (window as any).MessageBus.subscribe(
          (window as any).MessageBus.CHANNELS.TABLE_EVENT,
          `${sourceTable}.uploadAttachment`,
          (data: any, envelope: any) => {
            if (data.id === sourceTableId) {
              this.refresh();
            }
          });
      }

      // Put in a message bus subscription for updates to the parent record.
      this.updateRecordSubscription = (window as any).MessageBus.subscribe(
        (window as any).MessageBus.CHANNELS.TABLE_EVENT,
        `${sourceTable}.*`,
        (data: any, envelope: any) => {
          if (data.id === sourceTableId) {
            this.refresh();
          }
        });

      // Put in a message bus subscription for refreshes from Navigate.refresh()
      this.refreshListSubscription = (window as any).MessageBus.subscribe(
        (window as any).MessageBus.CHANNELS.REFRESH_EVENT,
        "*",
        (data: any, envelope: any) => {
            this.refresh();
        });

      // Support related list system templates
      this.koFieldRetainingObservables.koFieldViewModel.setRelatedListIds.subscribe((value) => {
        this.updateRelatedListIds = new Date().getTime().toString();
        this.refresh();
      });

      await this.loadCustomListDefinition();
      await this.loadCustomListSorting();

      // Id value will be a generic reference for Attachment
      const sourceTableIdForQuery = (targetTable === "Attachment") ?  sourceTableId + ":" + sourceTable : sourceTableId;

      const query = {
        and: [{
          relationName: reverseRelation,
          operator: "=",
          value: sourceTableIdForQuery
        }]
      };

      this.listCurrentQuery = JSON.stringify(query);

      // The sort order can come from the renderer properties for a related list.
      this.listCurrentSorted = this.listDataService.convertToDataGridSorted(this.listCustomSorting);
      sortOrder = this.listDataService.convertToControllerQuerySorted(this.listCurrentSorted);

      const rendererPropertiesSortOrder = this.koFieldRendererProperties[MagicRendererProperties.RELATED_LIST_SORT_ORDER];
      if (rendererPropertiesSortOrder) {
        // Format is something like "Order:desc,Number:asc"
        this.listCustomSorting = rendererPropertiesSortOrder;
        // We convert it into SF format [{ field: "Order", direction: "descending" }] as that is what this.listCurrentSorted wants.
        this.listCurrentSorted = this.listDataService.convertToDataGridSorted(rendererPropertiesSortOrder);

        // TODO we don't assign to the grid otherwise we cannot sort on fields that are not displayed in the grid
        // Assign to the grid - note that there is an inconsistency between the Sorts type and the SortSettingsModel type
        // regarding 'name' vs 'field'.
        // this.listSortSettings = {
        //   columns: this.listDataService.patchDataGridSortOrderToSortSettingsSortOrder(this.listCurrentSorted)
        // };

        // Assign for the query
        sortOrder = this.listDataService.convertToControllerQuerySorted(this.listCurrentSorted);
      }

      // Allow the custom fields to be stored in a renderer property
      const rendererPropertiesColumns: string = this.koFieldRendererProperties[MagicRendererProperties.RELATED_LIST_COLUMNS];
      if (rendererPropertiesColumns && rendererPropertiesColumns !== "") {
        this.listCustomFields = rendererPropertiesColumns.replace(/ /g, '').split(',');
      }

      // Allow a custom query to be stored in a renderer property. This is added to the parent relationship query.
      const rendererPropertiesQuery: string = this.koFieldRendererProperties[MagicRendererProperties.RELATED_LIST_QUERY];
      if (rendererPropertiesQuery && rendererPropertiesQuery !== "") {
        const customQuery = JSON.parse(rendererPropertiesQuery);
        const existingQuery = JSON.parse(this.listCurrentQuery);
        const newQuery = {
          and: [
            existingQuery,
            customQuery
          ]
        };
        this.listCurrentQuery = JSON.stringify(newQuery);
      }

      const controllerQuery = this.requestService.createControllerQuery();

      controllerQuery.firstResult(firstResult);
      controllerQuery.maxResults(maxResults);
      controllerQuery.sortOrder(sortOrder);
      controllerQuery.query(query);

      const data = {
        customFields: this.listCustomFields,
        parentTable: table,
        parentTableId: sourceTableId,
        relationName: relationName,
        applyAllowReadFromParent: true // TODO: where does this come from?
      };


      this.listCurrentDataContext = {
        type: this.type,
        table: targetTable,
        tileID: tileID,
        tile: tile,
        method: method,
        data: data,
      };

      try {
        result = await this.requestService.createRequest()
          .controller("servicely.core.System")
          .method(method)
          .table(targetTable)
          .tile(tile)
          .tileID(tileID)
          .aspect(aspect)
          .data(data)
          .controllerQuery(controllerQuery)
          .execute() as TableGridListContext;
      } catch (ex) {
        console.error("TableGridComponent.loadInitialData", this.patchListDataContext(this.listCurrentDataContext), data, ex);
      }
    }  else if (this.type === TableGridType.CustomRelatedList) {
      // This is a SystemController list request to get a custom related list on a detail form.
      this.customRelatedList = (window as any).Table("CustomRelatedList", this._customRelatedListId);

      this.customRelatedListTable = this.customRelatedList.Table();
      this.customRelatedListQuery = this.customRelatedList.Query();

      table = this.customRelatedListTable;
      tileID = null;
      tile = "ListAutoScaffold";
      method = "list";
      firstResult = 0;
      maxResults = 10;
      sortOrder = [];
      aspect = null; // TODO where do we get this from?

      this.listTableName = table;
      this.listAspect = aspect;

      await this.loadCustomListDefinition();
      await this.loadCustomListSorting();

      // The sort order can come from the renderer properties for a related list.
      this.listCurrentSorted = this.listDataService.convertToDataGridSorted(this.listCustomSorting);
      sortOrder = this.listDataService.convertToControllerQuerySorted(this.listCurrentSorted);

      const rendererPropertiesSortOrder = this.koFieldRendererProperties[MagicRendererProperties.RELATED_LIST_SORT_ORDER];
      if (rendererPropertiesSortOrder) {
        // Format is something like "Order:desc,Number:asc"
        this.listCustomSorting = rendererPropertiesSortOrder;
        // We convert it into SF format [{ field: "Order", direction: "descending" }] as that is what this.listCurrentSorted wants.
        this.listCurrentSorted = this.listDataService.convertToDataGridSorted(rendererPropertiesSortOrder);

        // TODO we don't assign to the grid otherwise we cannot sort on fields that are not displayed in the grid
        // Assign to the grid - note that there is an inconsistency between the Sorts type and the SortSettingsModel type
        // regarding 'name' vs 'field'.
        // this.listSortSettings = {
        //   columns: this.listDataService.patchDataGridSortOrderToSortSettingsSortOrder(this.listCurrentSorted)
        // };

        // Assign for the query
        sortOrder = this.listDataService.convertToControllerQuerySorted(this.listCurrentSorted);
      }

      // Allow the custom fields to be stored in a renderer property
      const rendererPropertiesColumns: string = this.koFieldRendererProperties[MagicRendererProperties.RELATED_LIST_COLUMNS];
      if (rendererPropertiesColumns && rendererPropertiesColumns !== "") {
        this.listCustomFields = rendererPropertiesColumns.replace(/ /g, '').split(',');
      }

      // Allow a custom query to be stored in a renderer property. This is added to the parent custom related list query.
      const rendererPropertiesQuery: string = this.koFieldRendererProperties[MagicRendererProperties.RELATED_LIST_QUERY];
      if (rendererPropertiesQuery && rendererPropertiesQuery !== "") {
        this.listCurrentQuery = rendererPropertiesQuery;
        query = JSON.parse(this.listCurrentQuery);
      }

      const controllerQuery = this.requestService.createControllerQuery();

      controllerQuery.firstResult(firstResult);
      controllerQuery.maxResults(maxResults);
      controllerQuery.sortOrder(sortOrder);
      controllerQuery.query(query);

      const data = {
        customFields: this.listCustomFields,
        customRelatedListQuery: this.customRelatedListQuery,
        customRelatedListParentId: this.customRelatedListParentId,
        customRelatedListParentTable: this.customRelatedListParentTable,
      };

      this.listCurrentDataContext = {
        type: this.type,
        table: table,
        tileID: tileID,
        tile: tile,
        method: method,
        data: data,
      };

      try {
        result = await this.requestService.createRequest()
          .controller("servicely.core.System")
          .method(method)
          .table(table)
          .tile(tile)
          .tileID(tileID)
          .aspect(aspect)
          .data(data)
          .controllerQuery(controllerQuery)
          .execute() as TableGridListContext;
      } catch (ex) {
        console.error("TableGridComponent.loadInitialData", this.patchListDataContext(this.listCurrentDataContext), data, ex);
      }
    }

    if (!result) {
      return;
    }

    const tdPromise = result.loadTableDefinition ? (window as any).TableManager.getRawTableDefinitionAsync(result.loadTableDefinition): null;

    const viewPromise = result.loadPrerenderedView ?
      fetch(`/_form_layout/form_renderer/${table}/${result.loadViewDefinition}`).then(res => res.json()) :
      null;

    const vdPromise = result.loadViewDefinition ?
      fetch(`/_application/view_definition/${result.loadViewDefinition}/${method}`).then(res => res.json()) :
      null;

    const promiseResults = await Promise.all([tdPromise, viewPromise, vdPromise]);
    if (promiseResults[0]) {
      result.tableDefinition = promiseResults[0];
    }
    if (promiseResults[1]) {
      result.view = promiseResults[1];
    }
    if (promiseResults[2]) {
      result = {...result, ...promiseResults[2]};
    }

    this.listModel = result;

    // PF - set the temporary variable used in the header and footer. This is until we get
    // a page content component. Until then this component is being its own page content.
    this.contentModel = new ListContentModel(this.listModel.viewDefinition, this.listModel.tableDefinition, this.listModel.data);

    this.contentModelSubscription = this.contentModel.changed$.subscribe((event) => {
      if (typeof event == 'string') {
        this.refresh();
      }
    });

    // Identify where we are in the UI
    this.contentModel.viewModel.setFormContext(this.listFormContext);

    // Make the query builder available to table operation scripts
    this.contentModel.viewModel.setQueryBuilder(this.listQueryBuilderViewModel);

    // Make the GridComponent available to table operations
    this.contentModel.setListGrid(this.listGrid);

    this.listColumns = this.generateCandidateColumns();

    const state: DataStateChangeEventArgs = {
      skip: this.listCurrentSkip,
      take: this.listCurrentTake,
      sorted: cloneDeep(this.listCurrentSorted)
    };

    // We feed back the data we have already retrieved in the initial tile call - will it contain the data for the custom view definition?
    this.listDataService.executeExplicitData(state, this.patchListDataContext(this.listCurrentDataContext), result.data, result.recordCount);

    // Alternative implementation to support formatted grouped results.
    // const dataResult = await this.listDataService.executeExplicitDataReturningPromise(state, this.patchListDataContext(this.listCurrentDataContext), result.data, result.recordCount);
    // this.listDataSource2 = new DataManager({ url: 'foo'/*json: result.result*/, adaptor: new ListDataAdaptor((dataResult as unknown as DataResult), this)});

    this.listPageSettings = {
      pageSize: maxResults,
      pageCount: 4,
      currentPage: 1,
      totalRecordsCount: result.recordCount,
    };

    // Mark initial load as having occurred
    this.hasInitialDataLoadCompleted = true;
  }

  initExportMenu() {
    this.listExportItems = [
      {
        text: $translate("ng.component.table-grid.export.clipboard")`Copy to clipboard`,
        id: 'copy',
        iconCss: "mi mi_file_copy"
      },
      {
        text: $translate("ng.component.table-grid.export.csv")`Download CSV`,
        id: 'csv',
        iconCss: "mi mi_note"
      },
      {
        text: $translate("ng.component.table-grid.export.xls")`Download Excel`,
        id: 'excel',
        iconCss: "mi mi_note"
      },
      {
        text: $translate("ng.component.table-grid.export.ppdf")`Download portrait PDF`,
        id: 'ppdf',
        iconCss: "mi mi_picture_as_pdf"
      },
      {
        text: $translate("ng.component.table-grid.export.lpdf")`Download landscape PDF`,
        id: 'lpdf',
        iconCss: "mi mi_picture_as_pdf"},
    ];
  }

  // Used as part of the async data retrieval
  public async dataStateChange(state: DataStateChangeEventArgs) {

    const patchedState = this.listDataService.patchDataGridSortOrder(state);

    // These are from the grid. If the grid has no sorting defined then we use the custom sorting if it is defined.
    this.listCurrentSorted = patchedState.sorted ? cloneDeep(patchedState.sorted) : this.listDataService.convertToDataGridSorted(this.listCustomSorting);
    this.listCurrentSkip = patchedState.skip;
    this.listCurrentTake = patchedState.take;

    this.listDataService.execute(patchedState, this.patchListDataContext(this.listCurrentDataContext));

    // Alternative implementation to support formatted grouped results.
    // const dataResult = await this.listDataService.executeReturningPromise(state, this.patchListDataContext(this.listCurrentDataContext));
    // this.listDataSource2 = new DataManager({ url: 'foo'/*json: result.result*/, adaptor: new ListDataAdaptor((dataResult as unknown as DataResult), this)});
  }

  generateCandidateColumns(fields?: any[]): ColumnModel[] {

    const isCustomFields = fields?.length > 0;

    const listOriginalColumns = this.listModel.viewDefinition.containers[0].elements;

    const selectionColumnWidth = this.SELECTION_COLUMN_WIDTH + "px";

    // Plays a role in supporting persistSelection
    const primaryKeyColumn: ColumnModel = {
      field: "primaryKey",
      type: "string",
      allowFiltering: false,
      allowSorting: false,
      allowResizing: false,
      isForeignColumn: () => false, // Needed to avoid a bug in syncfusion
      width: "0px",
      minWidth: "0px",
      maxWidth: "0px",
      visible: false,
      isPrimaryKey: true
    };

    const selectionColumn: ColumnModel = {
      field: "selection",
      type: "checkbox",
      allowFiltering: false,
      allowSorting: false,
      allowResizing: false,
      isForeignColumn: () => false, // Needed to avoid a bug in syncfusion
      clipMode: 'Clip',
      customAttributes: {
        class: 'sb-selection-column',
        style: {
        }
      },
      width: selectionColumnWidth,
    };

    const previewColumnWidth = this.PREVIEW_COLUMN_WIDTH + "px";

    const previewColumn: ColumnModel = {
      field: "preview",
      type: 'none',
      headerText: "",
      allowFiltering: false,
      allowSorting: false,
      allowResizing: false,
      isForeignColumn: () => false, // Needed to avoid a bug in syncfusion
      template: this.listPreviewTemplate as unknown as string,
      clipMode: 'Clip',
      customAttributes: {
        class: 'sb-preview-column',
        style: {
        }
      },
      width: previewColumnWidth,
    };

    const columnsToUse = isCustomFields ? fields :  listOriginalColumns;

    const candidateColumns = columnsToUse.map((col, idx) => {
      let type: ColumnType;
      let format;
      let formatType;
      const renderer = col.renderer;
      const fieldType = col.fieldType;
      const column = isCustomFields ? listOriginalColumns.filter(c => c.fieldName === col.fieldName)[0] || col : col;
      if (['duration', 'integer', 'long', 'decimal', 'order'].includes(fieldType)) {
        type = 'number';
      } else if(fieldType === 'money'){
        // Used for Excel export - UI Grid displayed or other exports value is overridden by the custom formatter
        type = 'number';
        const numberBuilder = this.getMoneyNumberBuilder(this.listModel, column.fieldName);
        format = numberBuilder.build()
      } else if (fieldType === 'boolean') {
        type = 'boolean';
      } else if (fieldType === 'date') {
        type = 'date';
        if (renderer === 'date') {
          format = this.i18nService.getLongDateFormatCLDR();
          formatType = 'date';
        } else if (renderer === 'datetime') {
          format = this.i18nService.getLongDateTimeFormatCLDR();
          formatType = 'dateTime';
        }
      } else if (fieldType === 'date_time') {
        type = 'dateTime';
        if (renderer === 'date') {
          format = this.i18nService.getLongDateFormatCLDR();
          formatType = 'date';
        } else if (renderer === 'datetime') {
          format = this.i18nService.getLongDateTimeFormatCLDR();
          formatType = 'dateTime';
        }
      } else {
        type = 'string';
      }

      const textAlign: TextAlign = ['integer', 'long', 'decimal', 'money'].includes(fieldType) ? 'Right' : 'Left';

      const firstColumnFormatter = {
        getValue: (column: Column, data: any): any => {
          let field = data._dataFn()[column.field];
          let recordField = data._dataFn().record[column.field];
          let style = field?.style;
          style = style ? [...style].join(" ") : ""; // Just in case style is a scalar
          const defaultFormattedValue = this.getFormattedCellValue(column, data);
          const aspect = this.listModel.viewDefinition.aspect ? `/${this.listModel.viewDefinition.aspect}` : "";
          // Choices have an optional icon and extra classes
          let icon = "";
          let css = "";
          if (field?.fieldType == "CHOICE") {
            const av = recordField?.availableValues?.[0];
            icon = av?.icon ? `<i class="mi">${av?.icon}</i> ` : "";
            css = av?.css || "";
          }
          return `<span class="${style} ${css} sb-link-truncate-target"><a href="/${data._dataFn().linkForUI}${aspect}">${icon}${defaultFormattedValue}</a></span>`;
        }
      };

      const otherColumnFormatter = {
        getValue: (column: Column, data: any): any => {
          let field = data._dataFn()[column.field];
          let recordField = data._dataFn().record[column.field];
          let style = field?.style;
          style = style ? [...style].join(" ") : ""; // Just in case style is a scalar
          const defaultFormattedValue = this.getFormattedCellValue(column, data);
          // Choices have an optional icon and extra classes
          let icon = "";
          let css = "";
          if (field?.fieldType == "CHOICE") {
            const av = recordField?.availableValues?.[0];
            icon = av?.icon ? `<i class="mi">${av?.icon}</i> ` : "";
            css = av?.css || "";
          }
          return `<span class="${style} ${css}">${icon}${defaultFormattedValue}</span>`;
        }
      };

      const columnModel: ColumnModel = {
        type: type,
        field: col.fieldName,
        headerText: this.i18nService.getMessage(col.i18nKey),
        allowEditing: !col.readOnly,
        displayAsCheckBox: true,
        isForeignColumn: () => false, // Needed to avoid a bug in syncfusion
        formatter: idx === 0 ? (firstColumnFormatter as unknown as Function) : (otherColumnFormatter as unknown as Function),
        textAlign: textAlign,
        disableHtmlEncode: false,
        clipMode: (idx === 0 || col.fieldType == 'text' || col.fieldType == 'code' || col.fieldType == 'html') ? 'Ellipsis' : 'EllipsisWithTooltip',
        allowResizing: true,
        customAttributes: {
          class: [`sb-${type}-column`, `sb-field-${fieldType}-column`, ...(idx === 0 ? [] : ['sb-ellipsis'])]
        },
        minWidth: "100px",
        allowFiltering: false, // By default no filtering which we relax later on
      };

      // Format dates in a special way. Also used for grouping.
      if (format) {
        if (typeof format === "string") {
          columnModel.format = {
            type: formatType,
            format: format
          } as DateFormatOptions;
          columnModel.enableGroupByFormat = true;
          // Grouping by formatted value does not work with an async data source but does with
          // a data manager, however there are problems with sorting of grouped columns.
          // Turned off for now
          columnModel.allowGrouping = false;
        } else {
          columnModel.format = format;
        }
      }

      if (columnModel.type == 'string') {
        columnModel.width = "auto";
      }

      // Filtering support, will be extended to other types
      if (['generic_reference', 'generic_reference_v2', 'workflow_transition'].includes(fieldType)) {

        columnModel.allowFiltering = false;

      } else if (fieldType === "reference") {

        columnModel.allowFiltering = true;
        columnModel.filter = {
          operator: 'contains'
        };

      } else if (columnModel.type == 'string') {

        columnModel.allowFiltering = true;
        columnModel.filter = {
          operator: 'contains'
        };

      } else if (columnModel.type == "number" && !['period', 'duration'].includes(fieldType)) {

        columnModel.allowFiltering = true;
        columnModel.filter = {
          operator: 'equal'
        };

      } else if (columnModel.type == "date") {

        columnModel.allowFiltering = true;
        columnModel.filter = {
          operator: 'equal'
        };

        const filterBarTemplate: TableGridFilterBarTemplate = {
          create: (args: { element: Element, column: Column }) => {
            filterBarTemplate.elem = document.createElement('input');
            return filterBarTemplate.elem;
          },
          write: (args: { element: Element, column: Column }) => {
            filterBarTemplate.component = new DatePicker({
              floatLabelType: 'Never',
              allowEdit: false,
              change: (args) => {
                let target: any = parentsUntil(args.element, 'e-filtertext');
                let columnName : string = target.id.replace('_filterBarcell', '');
                if (args.value) {
                  const formattedDate = this.i18nService.formatDate(args.value, { type: 'date', format: 'yyyy-MM-dd' });
                  this.listGrid.filterByColumn(columnName, 'equal', formattedDate);
                } else {
                  this.listGrid.removeFilteredColsByField(columnName);
                }
              },
            });
            filterBarTemplate.component.appendTo(filterBarTemplate.elem);
          }
        };
        columnModel.filterBarTemplate = filterBarTemplate;

      } else if (columnModel.type == "dateTime") {

        columnModel.allowFiltering = true;
        columnModel.filter = {
          operator: 'equal'
        };

        const filterBarTemplate: TableGridFilterBarTemplate = {
          create: (args: { element: Element, column: Column }) => {
            filterBarTemplate.elem = document.createElement('input');
            return filterBarTemplate.elem;
          },
          write: (args: { element: Element, column: Column }) => {
            filterBarTemplate.component = new DatePicker({
              floatLabelType: 'Never',
              allowEdit: false,
              change: (args) => {
                let target: any = parentsUntil(args.element, 'e-filtertext');
                let columnName : string = target.id.replace('_filterBarcell', '');
                if (args.value) {
                  // TODO: It shouldn't have to be this hard. SF Grid uses the Date constructor internally which is very picky
                  // TODO: about what it expects, so I have to use moment to get it into the correct format.
                  const formattedDate = moment(args.value).format("YYYY-MM-DDTHH:mm:ss.SSSZZ");
                  this.listGrid.filterByColumn(columnName, 'equal', formattedDate);
                } else {
                  this.listGrid.removeFilteredColsByField(columnName);
                }
              },
            });
            filterBarTemplate.component.appendTo(filterBarTemplate.elem);
          }
        };
        columnModel.filterBarTemplate = filterBarTemplate;

      } else if (columnModel.type == "boolean") {

        columnModel.allowFiltering = true;
        columnModel.filter = {
          operator: 'equal'
        };

        const filterBarTemplate: TableGridFilterBarTemplate = {
          create: (args: { element: Element, column: Column }) => {
            filterBarTemplate.elem = document.createElement('div');
            return filterBarTemplate.elem;
          },
          write: (args: { element: Element, column: Column }) => {
            filterBarTemplate.component = new DropDownList({
              dataSource: [
                { text: "Yes", value: true },
                { text: "No", value: false },
              ],
              floatLabelType: 'Never',
              allowFiltering: false,
              showClearButton: true,
              change: (args) => {
                let target: any = parentsUntil(args.element, 'e-filtertext');
                let columnName : string = target.id.replace('_filterBarcell', '');
                if (args.value) {
                  this.listGrid.filterByColumn(columnName, 'equal', args.value);
                } else {
                  this.listGrid.removeFilteredColsByField(columnName);
                }
              },
            });
            filterBarTemplate.component.appendTo(filterBarTemplate.elem);
          }
        };
        columnModel.filterBarTemplate = filterBarTemplate;

      }

      return columnModel;
    });

    // Don't show the selection or preview columns if these conditions are not met - based on list_binding.js and bindings.js
    if (!isCustomFields) {
      if (this.listModel.viewDefinition.properties?.suppressCheckboxes !== true || this.listModel.viwDefinition?.showListContextOperations !== false) {
        if (this.listModel.viewDefinition.properties?.suppressRowPreview !== true) {
          candidateColumns.unshift(previewColumn);
        }
        candidateColumns.unshift(selectionColumn);
      }
    }

    // Always include the primary key column at the end - it can't be at the beginning because the grid has extra padding-left
    // for the first child in a row.
    candidateColumns.push(primaryKeyColumn);

    return candidateColumns;
  }

  patchListDataContext(context: ListDataContext): ListDataContext {

  const columnQuery = this.generateColumnQuery();

    const readFromParentData = this.koTileContentView?.parentViewModel?.rendererProperties?.readFromParentData;

    return {...context,
      ...{query: this.listCurrentQuery,
        columnQuery: columnQuery,
        skip: this.listCurrentSkip,
        take: this.listCurrentTake,
        relatedListIds: this.relatedListCurrentIds,
        sortOrder: this.listCurrentSorted,
        customSortOrder: this.listCustomSorting,
        listModel: this.listModel,
        aspect: this.listAspect,
        readFromParentData: readFromParentData,
        aggregates: this.listDataAggregates,
      }
    };
  }

  generateColumnQuery() {
    const predicates = this.listGrid.filterSettings.columns;
    const originalColumns = this.listModel.viewDefinition.containers[0].elements;
    const originalFields = this.listModel.tableDefinition._fields;
    const originalRelationships = this.listModel.tableDefinition._relationships;
    const originalTable = this.listModel.tableDefinition._tableClass;
    const childHierarchy = this.listModel.tableDefinition._childHierarchy || [];
    const parentHierarchy = this.listModel.tableDefinition._parentHierarchy || [];
    const preferredLanguage = this.userService.getLanguage();

    const mappedPredicates = predicates.map((p) => {
      const originalColumn = originalColumns.find((c) => c.fieldName === p.field);
      const originalField = originalFields.find(f => f._fieldName == p.field);
      const originalRelationship = originalRelationships.find(f => f._relationshipName == p.field);

      if (p.value === null || p.value === undefined || p.value === "") {

        return null;

      } else if (originalColumn.relationType === "reference") {

        const targetTable = originalRelationship._targetTable;
        const displayValueField = originalRelationship._properties["default.displayValue"] || 'id';

        return {
          and: [
            {
              operator: "subQuery",
              targetTable: targetTable,
              targetField: "id",
              sourceField: p.field,
              condition: [
                {
                  and: [
                    {
                      operator: "like",
                      field: displayValueField,
                      value: p.value
                    }
                  ]
                }
              ]
            }
          ]
        };

      } else if (originalColumn.fieldType === "workflow_status") {

        return {
          and: [
            {
            operator: "subQuery",
            targetTable: "WorkflowStatus",
            targetField: "Code",
            sourceField: "WorkflowStatus",
            condition: [
              {
                and: [
                  {
                    operator: "in",
                    field: "Table",
                    value: [...childHierarchy, originalTable]
                  },
                  {
                    operator: "subQuery",
                    targetTable: "LocalizedMessage",
                    targetField: "I18nKey",
                    sourceField: "I18nKey",
                    condition: [
                      {
                        and: [
                          {
                            operator: "=",
                            field: "Locale",
                            value: preferredLanguage
                          },
                          {
                            operator: "like",
                            field: "Message",
                            value: p.value
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          }
          ]
        };

      } else if (originalColumn.fieldType === "choice") {

        return {
          operator: "subQuery",
          targetTable: "AvailableValue",
          targetField: "Key",
          sourceField: p.field,
          condition: [
            {
              and: [
                {
                  operator: "subQuery",
                  targetTable: "LocalizedMessage",
                  targetField: "I18nKey",
                  sourceField: "Value",
                  condition: [
                    {
                      and: [
                        {
                          operator: "=",
                          field: "Locale",
                          value: preferredLanguage
                        },
                        {
                          operator: "like",
                          field: "Message",
                          value: p.value
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        };

      } else if (originalColumn.fieldType === "classification") {

        return {
          and: [
            {
              operator: "subQuery",
              targetTable: "Classification",
              targetField: "id",
              sourceField: p.field,
              condition: [
                {
                  and: [
                    {
                      operator: "like",
                      field: "Path",
                      value: p.value
                    }
                  ]
                }
              ]
            }
          ]
        };
      } else if (originalColumn.fieldType === "date_time" || originalColumn.fieldType === "date") {

        const formattedDate = moment(p.value).format("YYYY/MM/DD");

        return  {
          fieldName: p.field,
          operator: "=",
          value: `day('${formattedDate}')`
        }

      } else {

        const field = p.field;
        let value = p.value;
        let operator = this.GRID_OPERATOR_TO_CONDITION_OPERATOR[p.operator.toLowerCase()];

        // For now, we want to ignore the filter expressions so that we can search for strings
        // containing '*' or '%', but only if the column model filter operator is normally 'contains'.
        // We also want to get the original cell text as SF will have stripped off the expression characters
        const shouldBeContainsOperator = (this.listGrid.columns as any[]).find((x) => x.field == p.field)?.filter.operator === 'contains';
        if (p.operator.toLowerCase() !== 'contains' && shouldBeContainsOperator) {
          operator = this.GRID_OPERATOR_TO_CONDITION_OPERATOR['contains'];
          value = (this.listGrid.filterModule as any).cellText[p.field];
        }

        return {
          fieldName: field,
          operator: operator,
          value: value
        }
      }
    }).filter( p => p != null);

    return JSON.stringify({and: mappedPredicates});
  }

  // Query builder methods

  fieldRenderInfo(data: any) {
    if (data && data.queryBuilder) {
      this.listQueryBuilderViewModel = data.queryBuilder;
      if (this.contentModel) {
        this.contentModel.viewModel.setQueryBuilder(data.queryBuilder);
      }

      if (this.listQueryBuilderViewModel) {
        this.listQueryBuilderViewModel.setTable(this.listTableName).then(() => {
          // We can now start listening to the query builder.
          this.listQueryBuilderIgnoreValueChange = false;
          // Set the current query in the query builder, but only if we are not on a new record as the query will refer to this record, ewhich doesn't yet exist.
          if ((this.type === TableGridType.RelatedList || this.type == TableGridType.CustomRelatedList) && this.koFieldRecordIsNewRecord) {
            // Don't set and apply a query. As this is a new record, trying to display related records will cause an error.
          } else {
            this.listQueryBuilderViewModel.setAndApplyInitialQuery(this.listCurrentQuery ? JSON.parse(this.listCurrentQuery) : undefined);
          }
        }).catch((ex) => {
          console.error("TableGridComponent.fieldRenderInfo error setting initial query", ex)
        });
      } else {
        console.warn("TableGridComponent.fieldRenderInfo listQueryBuilderViewModel not available");
      }

    }
  }

  // Context menu methods

  public listContextMenuClick(e: MenuEventArgs) {
    const actionCallback: ContextMenuActionCallback = (e.item as any)._actionCallback;
    if (actionCallback) {
      actionCallback(e);
    }
  }

  public async listContextMenuOpen(e: BeforeOpenCloseMenuEventArgs) {

    if ((e as any).column === null) {
      // context menu click on rows for toolbar or pager or 'No records to display'
      e.cancel = true;
      return;
    }

    // Preview has no context menu
    if ((e as any).column?.field === "preview") {
      // context menu click on eyeball or select checkbox
      e.cancel = true;
      return;
    }

    if (e.event && !(e.event as any)._synthetic) {
      const top = e.top;
      const left = e.left;
      const event = e.event;
      const target = e.event.target as HTMLElement;

      e.cancel = true;

      const contextMenuComponent = this.listGrid.contextMenuModule.contextMenu;

      const itemsToRemove: string[] = contextMenuComponent.items.map((i) => i.id).filter((i) => i !== this.LIST_CONTEXT_MENU_HIDDEN_ANCHOR_ID);

      // Because persistSelection is true, getSelectedRecords returns all the records selected on any page.
      const selectedRecords = this.listGrid.getSelectedRecords().map((record: any) => record._dataFn().record);

      const selectedFieldName = (e as any)?.column?.field;

      const isRelatedList = this.type === TableGridType.RelatedList;
      const isCustomRelatedList = this.type === TableGridType.CustomRelatedList;
      const relatedListParentId = isRelatedList ? this.koFieldRecord.id : this.customRelatedListParentId;
      const relatedListParentTable = isRelatedList ? this.koFieldRecord._tableClass : this.customRelatedListParentTable;
      const relatedListParentRecord = isRelatedList ? this.koFieldRootViewModel.getRecord() : this.customRelatedListParentRecord;

      // This will be the RelationName when the list is on a standard form and related to a record.
      // We will need to pass this to the frontend to allow the 'allow read from parent' functionality to work
      const relatedListParentRelation = isRelatedList ? this.koFieldDefinition.fieldName : null;

      const headerRow = !((e as any).rowInfo.rowData);
      const contextMenu: ContextMenu = headerRow ?
        this.createHeaderContextMenu(e) :
        this.tableOperationService.configureListContextMenu(
          this.contentModel,
          selectedRecords,
          selectedFieldName,
          isRelatedList || isCustomRelatedList,
          relatedListParentRecord,
          relatedListParentId,
          relatedListParentTable,
          relatedListParentRelation
        );

      this.mapContextMenu(contextMenu).then((itemsToAdd) => {

        // Only bulk operations will be displayed if selectedRecords is > 1, but if there are no operations we should say so.
        if (itemsToAdd.length === 0 && selectedRecords.length > 1) {
          itemsToAdd.push({
            text: 'No bulk record operations available',
            id: this.LIST_CONTEXT_MENU_NO_BULK_RECORD_OPERATIONS_ID
          });
        }

        contextMenuComponent.insertBefore(itemsToAdd, this.LIST_CONTEXT_MENU_HIDDEN_ANCHOR_ID, true);
        contextMenuComponent.removeItems(itemsToRemove, true);
        contextMenuComponent.enableItems([this.LIST_CONTEXT_MENU_NO_BULK_RECORD_OPERATIONS_ID], false, true);

        // Rearrange the icon span so that it supports MI ligatures
        Array.from(contextMenuComponent.element.children).forEach((n, i) => {
          const child = n.firstElementChild;
          if (child && child.tagName == 'SPAN') {
            const iconCss = contextMenuComponent.items[i].iconCss;
            if (child.classList.contains("mi") || child.classList.contains("fi")) {
              // Leave it alone as it already contains CSS for displaying the icon
            } else {
              // The classList needs to have "mi" and the text needs to become iconCss
              child.classList.remove(iconCss);
              child.classList.add("mi");
              child.textContent = iconCss;
            }
          }
        });

        // Mark the event so that when we reenter this method we don't recurse.
        (event as any)._synthetic = true;
        (contextMenuComponent as any).openMenu(null, null, top, left, event, target);
      });
    }
  }

  private async mapContextMenu(contextMenu: ContextMenu): Promise<ContextMenuItemModel[]> {
    const contextMenuItems = await contextMenu.getMenuItems();

    const menuItems: Promise<ContextMenuItemModel>[] = contextMenuItems.map(async (c) => {
      if (c.isSeparator) {
        return {
          id: this.utilService.generateCSSID(),
          separator: true
        }
      }
      const menuItem: ContextMenuItemModel = {
        id: this.utilService.generateCSSID(),
        iconCss: c.prefixIcon ? c.prefixIcon : "mi mi-fw", // Putting anything here will cause the menu item to be indented correctly.
        text: c.label,
      };

      (menuItem as any)._labelSuffix = c.labelSuffix;
      (menuItem as any)._actionCallback = c.actionCallback;

      if (c.childMenu) {
        menuItem.items = await this.mapContextMenu(c.childMenu);
      }

      return menuItem;
    });

   return Promise.all(menuItems);
  }

  private createHeaderContextMenu(e: BeforeOpenCloseMenuEventArgs): ContextMenu {
    const column = (e as any).column;
    const fieldName = column.field;
    if (fieldName === "selection") {
      return this.contextMenuService.createContextMenu().addContextMenuItems(
        [

          this.contextMenuService.createContextMenuItem(
            $translate("ng.component.table-grid.selection.clear-page")`Clear page selection`,
            (event) => {
              this.listGrid.contextMenuModule.isOpen = false;
              // Clears on the current page
              this.listGrid.clearRowSelection();
            },
            undefined,
            "cancel"),

          this.contextMenuService.createContextMenuItem(
            $translate("ng.component.table-grid.selection.clear-list")`Clear list selection`,
            (event) => {
              this.listGrid.contextMenuModule.isOpen = false;
              // Clears on all current pages, apparently.
              this.listGrid.clearSelection();
              this.onSelected(null);
            },
            undefined,
            "cancel"),
        ]
      );
    } else {
      const contextMenu =  this.contextMenuService.createContextMenu();

      if (this.type !== TableGridType.List) {
        contextMenu.addContextMenuItems(
          [
            this.contextMenuService.createContextMenuItem(
              this.listShowToolbar === false ?
                $translate("ng.component.table-grid.toolbar.toggle")`Show toolbar` :
                $translate("ng.component.table-grid.toolbar.hide")`Hide toolbar`,
              (event) => {
                this.listGrid.contextMenuModule.isOpen = false;
                this.listShowToolbar = !this.listShowToolbar;
                if (!this.listShowToolbar) {
                  this.showColumnFilters = false;
                }
              },
              undefined,
              "toolbar"),
          ]
        );
        contextMenu.addContextMenuSeparator();
      }

      contextMenu.addContextMenuItems(
        [

          this.contextMenuService.createContextMenuItem(
            $translate("ng.component.table-grid.sort.ascending")`Sort ascending`,
            (event) => {
              this.listGrid.contextMenuModule.isOpen = false;
              this.listGrid.sortColumn((e as any).column.field,
                'Ascending',
                this.listGrid.sortSettings.columns.length > 0);
            },
            undefined,
            "sort"),

          this.contextMenuService.createContextMenuItem(
            $translate("ng.component.table-grid.sort.descending")`Sort descending`,
            (event) => {
              this.listGrid.contextMenuModule.isOpen = false;
              this.listGrid.sortColumn((e as any).column.field,
                'Descending',
                this.listGrid.sortSettings.columns.length > 0);

            },
            undefined,
            "sort"),

          this.contextMenuService.createContextMenuItem(
            $translate("ng.component.table-grid.sort.clear")`Clear sorting`,
            (event) => {
              this.listGrid.contextMenuModule.isOpen = false;
              this.listGrid.clearSorting();
            },
            undefined,
            "cancel"),
        ]
      );

      if (column.type === "number") {
        if (this.userService.getBooleanProperty("list.aggregation.enabled", true)) {
          contextMenu.addContextMenuSeparator();
          const items = ["Count", "Sum", "Min", "Max", "Average"].map((type: AggregateType) => {
            return this.contextMenuService.createContextMenuItem(
              $translate(`ng.component.table-grid.aggregation.${type.toLowerCase()}`)`${type}`,
              (event) => {
                this.listGrid.contextMenuModule.isOpen = false;
                if (this.hasAggregation(fieldName, type)) {
                  this.removeAggregation(fieldName, type);
                } else {
                  this.addAggregation(fieldName, type);
                }
              },
              undefined,
              this.hasAggregation(fieldName, type) ? "check" : undefined)
          });
          items.push(this.contextMenuService.createContextMenuItem(
            $translate("ng.component.table-grid.aggregation.clear")`Clear all aggregations`,
            (event) => {
              this.listGrid.contextMenuModule.isOpen = false;
              this.clearAggregation();
            },
            undefined,
            "cancel"));
          contextMenu.addContextMenuItems(items);
        }
      }

      return contextMenu;
    }
  }

  // Preview

  clickPreviewAction(e: MouseEvent, data: any) {
    if (e.metaKey === true || e.altKey === true || e.ctrlKey === true) {
      this.altClickPreviewAction(e, data);
    } else {
      const tableName = data._dataFn().tableClass;
      const tableID = data._dataFn().id;
      const aspect = this.listModel.viewDefinition.aspect;
      const view = "Popout" + tableName;
      const relationshipData = data._dataFn().relationshipData;

      this.navigateService.rightSlideInWindow(
        {
          'tile': 'DetailsAutoScaffold',
          'parameter-values': {
            table: tableName,
            id: tableID,
            aspect: aspect,
            view: view
          },
          'loadOptions': {
            cache: false,
            popup: true
          }
        },
        (eventData, envelope) => {
          if (eventData.envelope && eventData.envelope.channel === (window as any).MessageBus.CHANNELS.TABLE_EVENT) {
            this.updateDataFromEventUpdate(eventData.data.tableName, eventData.data.id, eventData.data.eventName, eventData.data.data, relationshipData);
            if (this.relatedListUpdateCallbackFunction !== undefined && typeof this.relatedListUpdateCallbackFunction === "function") {
              this.relatedListUpdateCallbackFunction(eventData.data.tableName, eventData.data.id, eventData.data.eventName, eventData.data.data, relationshipData);
            }
          }
        });
    }
  }

  altClickPreviewAction(e: MouseEvent, data: any) {
    const tableName = data._dataFn().tableClass;
    const tableID = data._dataFn().id;
    const aspect = this.listModel.viewDefinition.aspect;
    const identifier = data._dataFn().identifier;

    this.navigateService.to({
      table: tableName,
      id: identifier,
      aspect: aspect
    });
  }

  // TODO: This mucking about with state on the element should be done with an Angular directive.
  clickPreview(e: MouseEvent, data: any) {

    const initClicks = (e: MouseEvent) => {
      if ((e.target as any)._clicks === null || (e.target as any)._clicks === undefined) {
        (e.target as any)._clicks = 0;
      }
    };
    const getClicks = (e: MouseEvent): number => {
      return (e.target as any)._clicks;
    };
    const setClicks = (e: MouseEvent, value: number) => {
      (e.target as any)._clicks = value;
    }

    initClicks(e);
    setClicks(e, getClicks(e) + 1);
    if (getClicks(e) === 1) {
      setTimeout(() => {
          if (getClicks(e) === 1) {
            this.clickPreviewAction(e, data);
          } else {
            this.altClickPreviewAction(e, data);
          }
        setClicks(e, 0);
      }, 200);
    }
    return false;
  }

  // Grid rendering support

  viewProperties() {
    return this.listModel?.viewDefinition?.properties;
  }

  tableProperties() {
    return this.listModel?.tableDefinition?._properties;
  }

  viewProperty(key: string) {
    return this.listModel?.viewDefinition?.properties?.[key];
  }

  tableProperty(key: string) {
    return this.listModel?.tableDefinition?._properties?.[key];
  }

  // Cell rendering support

  uniqueFieldId(data: any) {
    return `${this.recordId(data)}.${this.fieldName(data)}`;
  }

  fieldType(data: any) {
    return this.getElement(data)?.fieldType;
  }

  fieldName(data: any) {
    return data.column.field;
  }

  fieldValue(data: any) {
    const fieldName = data.column.field;
    const fieldValue = data[fieldName];
    return fieldValue?.value;
  }

  fieldDisplayValue(data: any) {
    const fieldName = data.column.field;
    const fieldValue = data[fieldName];
    return fieldValue?.displayValue;
  }

  renderer(data: any) {
    return this.getElement(data)?.renderer;
  }

  rendererIs(data: any, renderer: string) {
    return this.getElement(data)?.renderer.toLowerCase() === renderer.toLowerCase();
  }

  properties(data: any) {
    return this.getElement(data)?.properties || {};
  }

  rendererProperties(data: any) {
    return this.getElement(data)?.rendererProperties || {};
  }

  tableName(data: any) {
    return data._dataFn().listModel.viewDefinition.table;
  }

  recordId(data: any) {
    return data._dataFn().id;
  }

  private getElement(data: any) {
    const fieldName = data.column.field;
    const viewDefinition = data._dataFn().listModel.viewDefinition;
    return viewDefinition.containers[0].elements.find((e) => e.fieldName === fieldName);
  }

  // Component event handlers

  listDisplaySelect(e: MenuEventArgs) {
    const pageSize = Number(e.item.id);
    this.listGrid.pagerModule.setPageSize(pageSize);
    this.listDisplaySizesTitle = $translate("ng.component.table-grid.display")`Display ${pageSize}`;
    this.userService.updateUserProfileSetting("list.pagination.size", String(pageSize));
  }

  listRefresh(e: Event) {
    this.refresh();
    return false;
  }

  async listToolbarClick(e: ClickEventArgs) {
    if (e.item.id === 'Refresh') {
      this.refresh();
    } else if (e.item.id === 'Filter') {
      this.listShowQueryBuilder = !this.listShowQueryBuilder;
      this.listQueryBuilderViewModel && this.listQueryBuilderViewModel.isVisible(true);
    } else if (e.item.id === "SavedFilterSave") {
      const filterName = (this.savedFiltersFilterName as any).nativeElement.value;
      const filterDef = this.savedFiltersListViewData.find((f) => { return f.text === filterName });
      const filterValue = this.listQueryBuilderViewModel.getQueryObjectJson();
      if (filterDef) {
        // We have found a filter with the same name so replace the filter value
        filterDef.filter = filterValue;
      } else {
        // Push the newly named filter just before the 'New'
        const newList = cloneDeep(this.savedFiltersListViewData);

        newList.splice(
          this.savedFiltersListViewData.length - 1,
          0,
          {
            text: filterName,
            id: this.utilService.generateCSSID(),
            filter: filterValue}
        );

        this.savedFiltersListViewData = newList;
      }

      const filterList =  JSON.stringify(this.savedFiltersListViewData.slice(0, this.savedFiltersListViewData.length - 1));
      // Save the current filter to storage and hide the edit controls
      const savedFilters = await this.saveUserProperty(this.getSavedFiltersListDefinitionKey(), filterList);
      this.listShowQueryBuilder = false;
      this.savedFiltersEdit = false;
    } else if (e.item.id === "SavedFilterCancel") {
      this.listShowQueryBuilder = false;
      this.savedFiltersEdit = false;
    } else if (e.item.id === 'clearFilters') {
      this.listQueryBuilderViewModel && this.listQueryBuilderViewModel.setAndApplyInitialQuery(this.QUERY_EMPTY_JSON);
    } else if (e.item.id == "toggleColumnFilters") {
      this.showColumnFilters = !this.showColumnFilters;
    } else if (e.item.id == "toggleGrouping") {
      this.allowGrouping = !this.allowGrouping;
      if (!this.allowGrouping) {
        this.listGrid.groupModule.clearGrouping();
      }
    }
  }

  async listGetAllRecordsForExport(fields: string[]) {
    const table = this._koTileContentView.table;
    const tileID = this._koTileContentView.tileID;
    const tile = this._koTileContentView.tile;
    const method = this._koTileContentView.method;
    const aspect = this.listAspect;
    const firstResult = 0;
    const maxResults = 10000;
    const sortOrder = this.listDataService.convertToControllerQuerySorted(this.listCurrentSorted);

    const query = this.requestService.createControllerQuery();

    query.firstResult(firstResult);
    query.maxResults(maxResults);
    query.sortOrder(sortOrder);

    // Similar logic to ListDataService.getData for handling query
    let jsonQuery = this.listCurrentQuery && this.listCurrentQuery != this.QUERY_EMPTY ? JSON.parse(this.listCurrentQuery) : undefined;

    let columnQuery = this.generateColumnQuery();

    columnQuery = columnQuery && columnQuery != this.QUERY_EMPTY ? JSON.parse(columnQuery) : undefined;

    if (columnQuery && jsonQuery) {
      jsonQuery = {and: [jsonQuery, columnQuery]};
    } else if (columnQuery) {
      jsonQuery = columnQuery;
    }

    query.query(jsonQuery);

    const requestedFields = fields?.length > 0 ? fields : this.listCustomFields;

    const data = {
      customFields: requestedFields
    };

    try {
      return await this.requestService.createRequest()
        .table(table)
        .tile(tile)
        .tileID(tileID)
        .method(method)
        .aspect(aspect)
        .controllerQuery(query)
        .data(data)
        .execute() as TableGridListContext;
    } catch (ex) {
      console.error("TableGridComponent.listGetAllRecordsForExport", this.patchListDataContext(this.listCurrentDataContext), ex);
    }
  }

  async listExportMenuSelect(e: MenuEventArgs) {
    this.listCustomFieldsExportShowDialog(e.item.id);
  }

  async listExportSelect(type: string, fields: string[]) {
    const isCustomFields = fields?.length > 0;

    // PDF requires special handling otherwise it doesn't format properly on the page
    const currentColumns = this.listGrid.columns;

    try {
      const filename = this.i18nService.getMessage(this.listModel.viewDefinition.title).replace(' ', '-') + '-export-' + moment().format("YYYY-MM-DD-HH-mm");

      const originalColumns = this.listModel.viewDefinition.containers[0].elements;

      const systemField = {
        id: 'core.id.name',
        Id: 'core.id.name',
        SID: 'core.sid.name',
        CreatedOn: 'core.createdon.name',
        CreatedBy: 'core.createdby.name',
        UpdatedOn: 'core.updatedon.name',
        UpdatedBy: 'core.updatedby.name',
        Version: 'core.version.name',
        TableClass: 'core.tableclass.name',
        Application: 'core.application.name',
      };

      const managedField = {
        WorkflowStatus: 'core.workflowstatus.name',
        PreviousTransition: 'core.previoustransition.name',
        PreviousWorkflowStatus: 'core.previousworkflowstatus.name',
        QuestionsSource: 'core.questionssource.name',
      };
      // Get the table definition for the table and construct what is needed for the export
      const table = (window as any).TableManager.getTableDefinition(this.listTableName);
      const tmFields = Object.values(table.getFields()).map((f: any) => {
        const fieldName = f.getName() == 'id' ? 'Id' : f.getName();
        const fieldType = f.getFieldType().toLowerCase();
        const i18nKey = f.getI18nKey() || systemField[fieldName] || managedField[fieldName] || '';
        const renderer = originalColumns.find(x => x.fieldName == fieldName)?.renderer || f.getRenderer() || this.tableMetadataService.allFieldTypes()[fieldType]?.defaultRenderer || 'textfield';
        return {
          renderer: renderer,
          fieldType: fieldType,
          fieldName: fieldName,
          i18nKey: i18nKey,
          readOnly: f.isReadOnly()
        };
      })

      // Probably don't want to use any of these as the relevant relationships are in tmFields.
      const tmRelations = Object.values(table.getRelations()).map((f: any) => {
        const fieldName = f.getName();
        const fieldType = f.getRelationshipType().toLowerCase();
        const renderer = originalColumns.find(x => x.fieldName == fieldName)?.renderer || f.getRenderer() || this.tableMetadataService.allFieldTypes()[fieldType]?.defaultRenderer || 'textfield';
        const i18nKey = f.getI18nKey() || systemField[fieldName] || managedField[fieldName] || '';
        return {
          renderer: renderer,
          fieldType: fieldType,
          fieldName: fieldName,
          i18nKey: i18nKey,
          readOnly: f.isReadOnly()
        };
      })

      const exportFields = fields.map(f => tmFields.find(field => field.fieldName == f));

      const exportColumns = this.generateCandidateColumns(exportFields);

      // PDF requires special handling otherwise it doesn't format properly on the page.
      // The grid itself needs to have the export columns applied so that it can calculate the column widths.
      // We set then set the original columns back when done. This causes the grid to flash.
      if (isCustomFields) {
        if (type === "ppdf" || type === "lpdf") {
          this.listGrid.columns = exportColumns;
          this.resizeColumns(true);
          await this.utilService.sleep(500);
        }
      } else {
        this.listColumnsToIgnore.map((c) => this.listGrid.getColumnByField(c)).forEach((c) => c.visible = false);
      }

      const result = await this.listGetAllRecordsForExport(fields);

      const state: DataStateChangeEventArgs = {
        skip: 0,
        take: 10000,
        sorted: cloneDeep(this.listCurrentSorted)
      };

      const dataSource = await this.listDataService.getExplicitData(state, this.patchListDataContext(this.listCurrentDataContext), result.data, result.recordCount).toPromise();
      const formattedFieldType = this.getFormattedTypes(type);

      const formattedResult = (dataSource as any).result.map((r) => {
        Object.keys(r).forEach((k) => {
          if (k !== 'primaryKey' && k !== 'Id') {
            const column = (this.listGrid.columns as any).find((c) => c.field === k);
            const _data = r._dataFn() || {}
            const _field = _data[k] || {}
            const fieldType = _field.fieldType;
            if (column && formattedFieldType.includes(fieldType)) {
              r[k] = this.getFormattedCellValue(column, r);
            }
            if (fieldType === 'MONEY' && type === 'excel') {
              // Decimal object is not well managed by syncfusion for Excel - switch to Number
              r[k] = !Number.isNaN(_field.value) ? Number(_field.value) : 0;
            }
          }
        });
        return r;
      });

      const dataManager: DataManager = new DataManager(formattedResult);

      if (type === 'copy') {
        const properties: ExcelExportProperties = {
          exportType: "AllPages",
          dataSource: dataManager,
        };
        if (isCustomFields) {
          properties.columns = (exportColumns as unknown as Column[]);
        }
        this.listGrid.csvExport(properties, false, null, true).then((workbook) => {
          // PF I can't find the type definition for workbook
          if (workbook) {
            const sheet = workbook.worksheets?.[0];
            if (sheet) {
              const data = sheet.rows.map((row) => {
                return row.cells.map((cell) => {
                  return cell.value;
                }).join("\t");
              }).join("\n") + "\n";

              // Put data on clipboard in a hacky way using the grid clipboard module.
              const clipBoardTextArea = (this.listGrid.clipboardModule as any).clipBoardTextArea;
              clipBoardTextArea.value = data;
              clipBoardTextArea.focus();
              if (!Browser.userAgent.match(/ipad|ipod|iphone/i)) {
                clipBoardTextArea.select();
              } else {
                clipBoardTextArea.setSelectionRange(0, clipBoardTextArea.value.length);
              }
              document.execCommand('copy');
              clipBoardTextArea.blur();
              window.getSelection().removeAllRanges();
            }
          }
        });
      } else if (type === 'csv') {
        const properties: ExcelExportProperties = {
          fileName: filename + ".csv",
          exportType: "AllPages",
          dataSource: dataManager,
        };
        if (isCustomFields) {
          properties.columns = (exportColumns as unknown as Column[]);
        }
        await this.listGrid.csvExport(properties);
      } else if (type === 'excel') {
        const properties: ExcelExportProperties = {
          fileName: filename + ".xlsx",
          exportType: "AllPages",
          dataSource: dataManager,
        };
        if (isCustomFields) {
          properties.columns = (exportColumns as unknown as Column[]);
        }
        await this.listGrid.excelExport(properties);
      } else if (type === 'ppdf') {
        const theme = await this.generatePdfExportTheme();

        const properties: PdfExportProperties = {
          fileName: filename + ".pdf",
          exportType: "AllPages",
          dataSource: dataManager,
          pageSize: "A4",
          pageOrientation: "Portrait",
          theme: theme
        };
        await this.listGrid.pdfExport(properties);
      } else if (type === 'lpdf') {
        const theme = await this.generatePdfExportTheme();

        const properties: PdfExportProperties = {
          fileName: filename + ".pdf",
          exportType: "AllPages",
          dataSource: dataManager,
          pageSize: "A4",
          pageOrientation: "Landscape",
          theme: theme
        };
        await this.listGrid.pdfExport(properties);
      }
    } finally {
      // PDF requires special handling otherwise it doesn't format properly on the page
      if (isCustomFields) {
        if (type === "ppdf" || type === "lpdf") {
          this.listGrid.columns = currentColumns;
          this.resizeColumns(true);
        }
      }
    }
  }

  private getFormattedTypes(exportType: string): string[] {
    if (exportType === 'excel') {
      //Exclude Money from formatting and keep it as a number - to allow easy formula manipulation and leverage Excel formatting instead
      return ['PERCENTAGE'];
    }
    return ['MONEY', 'PERCENTAGE'];
  }

  private async generatePdfExportTheme() {
    const result = await fetch("fonts/Helvetica.ttf.base64");
    const base64FontData = await result.text();
    return {
      header: {
        font: new PdfTrueTypeFont(base64FontData, 10)
      },
      caption: {
        font: new PdfTrueTypeFont(base64FontData, 10)
      },
      record: {
        font: new PdfTrueTypeFont(base64FontData, 10)
      },
    };
  }

  // Grid event handlers

  dataBound(e: Object) {
      this.resizeColumns(true);
      this.initialDataVisibility = {};
      this.onCreatedPagerButtonSupport(e);
  }

  onCreated(e: Object) {
    this.onCreatedContextMenuSupport(e);
    this.onCreatedPagerButtonSupport(e);
    this.onCreatedPagerExternalMessageSupport(e);
  }

  onSelected(e: any) {
    const count = this.listGrid.getSelectedRecords()?.length || 0;
    const message = count ? $translate("ng.component.table-grid.selected")`(${count} selected)` : this.NBSP;
    this.listGrid.pagerModule.pagerObj.externalMessage = message;
  }

  excelExportComplete(e: ExcelExportCompleteArgs) {
    this.listColumnsToIgnore.map((c) => this.listGrid.getColumnByField(c)).filter(c => !!c).forEach((c) => c.visible = true);
  }

  pdfExportComplete(e: PdfExportCompleteArgs) {
    this.listColumnsToIgnore.map((c) => this.listGrid.getColumnByField(c)).filter(c => !!c).forEach((c) => c.visible = true);
  }

  excelQueryCellInfo(args: ExcelQueryCellInfoEventArgs): void {
    const columnFormat = args?.column?.format
    if (columnFormat) {
      const numberFormat = columnFormat as NumberFormatOptions
      if (numberFormat?.format === 'A') {
        args.style = args.style || {};
        // Force number format for money field as somehow the currency and symbol are not taken into account
        // Not working 100% of the time, some format options seems to cause the worksheet to be repaired ignoring the formatting
        // but still with workable numbers
        args.style.numberFormat = this.i18nService.getNumberPattern(numberFormat, true);
      }
    }
  }

  onRefresh(e: boolean) {
    this.refresh();
  }

  private onCreatedContextMenuSupport(e: Object) {

    const self = this;
    const contextMenuModule = this.listGrid.contextMenuModule;
    const contextMenu = contextMenuModule.contextMenu;

    // Make sure the context menu hidden anchor is really hidden
    contextMenu.hideItems([this.LIST_CONTEXT_MENU_HIDDEN_ANCHOR_ID], true);

    // The context menu will close if there is a scroll event. These are very likely to happen for
    // related lists so we cancel the close if we get one. You csn still close by clicking away or by selecting
    // a menu item.
    if (this.type === TableGridType.RelatedList) {
      contextMenu.beforeClose = (e: BeforeOpenCloseMenuEventArgs) => {
        if (e.event.type === "scroll") {
          e.cancel = true;
        }
      }
    }

    // Make sure that the row selected when opening the context menu is deselected when the menu is closed.
    const originalOnClose = (contextMenuModule as any).contextMenuOnClose;
    contextMenu.onClose = (contextMenuModule as any).contextMenuOnClose = (e: OpenCloseMenuEventArgs) => {
      const lastSelectedRowIndex = self.listGrid.selectedRowIndex;
      if (lastSelectedRowIndex !== null && lastSelectedRowIndex !== undefined) {
        const newSelections = this.listGrid.getSelectedRowIndexes().filter((x) => x !== lastSelectedRowIndex);
        self.listGrid.selectRows(newSelections);
        self.onSelected(null);
      }
      originalOnClose && originalOnClose.call(contextMenuModule, e);

      // SF doesn't seem to set this when it should. The sorting by clicking in the header does not work
      // if it thinks the context menu is open.
      contextMenuModule.isOpen = false;
    }
  }

  private onCreatedPagerButtonSupport(e: Object) {

    // Create some buttons in the pager on the left to match what the old UI did. There is no easy way to inject
    // Angular components into the pager so we build the buttons the old way, and then we hide the pager messages.
    if (this.type === TableGridType.RelatedList) {
      const self = this;
      const pagerObj = this.listGrid.pagerModule.pagerObj;
      const pagerRangeMsg: HTMLElement = (pagerObj.pagerMessageModule as any).pageNoMsgElem;

      // The pager incorrectly hides the buttons because the algorithm used calculates their widths incorrectly.
      // This might be triggered by all the mucking around we do below with the pager.
      // This is probably fixed in a later release.
      const originalResizePagerFn = (pagerObj as any).resizePager; // Private method
      if (!originalResizePagerFn.patched) {
        (pagerObj as any).resizePager = () => {
          originalResizePagerFn.apply(pagerObj);
          (pagerObj.element.querySelectorAll(".e-parentmsgbar")[0] as HTMLElement).style.display = '';
        };
        (pagerObj as any).resizePager.patched = true;
        (pagerObj as any).unwireEvents(); // Private method
        (pagerObj as any).wireEvents(); // Private method
      }

      if (!this.createButton) {
        const createButton = document.createElement("div") as HTMLDivElement;
        createButton.classList.add("e-btn", "e-small", "e-primary", "sb-grid-related-list-button");
        createButton.addEventListener("click", (ev: MouseEvent): any => {

          // This event listener is somehow hanging around after destruction. We want to make sure it
          // will not do anything if it does activate.
          if (self.isDestroyed) return;

          // Based from related_list_renderer.js
          const sourceTable = self._koFieldViewModel.getTableName();
          const fieldName = self._koFieldViewModel.getName();
          const recordId = self.koFieldRecord.id;
          const recordTable = self.koFieldRecord._tableClass;
          const aspect = self.listModel.viewDefinition.aspect;
          const callback = (id: string, table: string, _: any) => {

            // This event listener is somehow hanging around after destruction. We want to make sure it
            // will not do anything if it does activate.
            if (self.isDestroyed) return;

            // Update the parent reverse relation record explicitly, because if it is not on the form then it won't get sent back.
            const targetRelationName = self.koFieldDefinition.fieldName;
            const targetRelation = (window as any).TableManager.getTableDefinition(sourceTable).getRelation(targetRelationName);
            const targetTable = targetRelation.getTargetTable();
            const reverseRelation = targetRelation.getReverseRelationName();

            const relationshipType = (window as any).TableManager.getTableDefinition(targetTable)?.getRelation(reverseRelation)?.getRelationshipType();
            const data: any = {id: id};

            // For a generic reference we have a choice of having the table class of the parent table where the relation is defined
            // or the table class of the child table that is displaying the related list.
            // An example is RiskAssessmentItem where we want the table class of the question generic references to
            // be RiskAssessmentItem rather than QuestionItem. We use a renderer property to drive this.
            const grTable = (this.koFieldRendererProperties['child.table.for.generic.reference'] === 'true') ? recordTable : sourceTable;
            if (relationshipType === "generic_reference" || relationshipType === "generic_reference_v2") {
              data[reverseRelation] = `${recordId}:${grTable}`;
            } else {
              data[reverseRelation] = recordId;
            }

            this.requestService.createRequest()
              .controller("servicely.core.System")
              .method("updateRelatedIDIfRequired")
              .table(targetTable)
              .data(data)
              .execute()
              .then((result) => {
                self.listGrid?.refresh();
                self.koFieldRootViewModel.getEventExecutor().onRelationUpdate(self.koFieldRetainingObservables.koFieldViewModel);
              })
              .catch((e) => {
                console.error("TableGridComponent.createButtonCallback - update parent id", e);
              });
          }
          (window as any).KO_RENDERER_ELEMENTS.addRelatedTable(sourceTable, recordId, fieldName, aspect, callback);

          ev.preventDefault();
        });
        createButton.appendChild(document.createTextNode(this.i18nService.getMessage("core.relatedlist.createnew.relation")));
        this.createButton = createButton;
      }

      if (!this.quickEntryButton) {
        const quickEntryButton = document.createElement("div") as HTMLDivElement;
        quickEntryButton.classList.add("e-btn", "e-small", "sb-grid-related-list-button");

        quickEntryButton.addEventListener("click", (ev: MouseEvent): any => {
          // This event listener is somehow hanging around after destruction. We want to make sure it
          // will not do anything if it does activate.
          if (self.isDestroyed) return;

          ev.preventDefault();

          // Update the form
          const callback = () => {
            // This event listener is somehow hanging around after destruction. We want to make sure it
            // will not do anything if it does activate.
            if (self.isDestroyed) return;

            // self.koFieldRetainingObservables.koFieldViewModel.value() contains the current set of values which haven't been sent to the server yet.
            self.relatedListCurrentIds = self.koFieldRetainingObservables.koFieldViewModel.value().map((v) => {
              return (typeof v.id === "function" ? v.id() : v.id);
            });

            self.listGrid?.refresh();

            // When a related list appears in a system template it doesn't have a real parent record and also doesn't get an
            // event executor
            if (self.koFieldRootViewModel.getEventExecutor) {
              self.koFieldRootViewModel.getEventExecutor()?.onRelationUpdate(self.koFieldRetainingObservables.koFieldViewModel);
            }
          };

          const loadDataCallback = function () {

            // Build the content element
            const div = document.createElement("div");
            (window as any).KO_FIELD_RENDERER.onetomany.EDIT(div,
              self.koFieldRetainingObservables.koFieldViewModel,
              self.koFieldRetainingObservables.koFieldRendererProperties,
              self.koFieldRetainingObservables.koFieldDefinition,
              self.koFieldRetainingObservables.koFieldRecord,
              self.koFieldRetainingObservables.koFieldFormDisplayType,
              self.koFieldRetainingObservables.koFieldRootViewModel);

            // Pop up the dialog
            const dialog = DialogUtility.alert({
              title: $translate("ng.component.table-grid.add-remove-relations.title")`Add/remove relations`,
              position: {
                X: 'center',
                Y: 'center',
              },
              isModal: true,
              width: "800px",
              okButton: {
                text: $translate("ng.component.table-grid.add-remove-relations.close")`Close`,
                click: () => {
                  callback();
                  dialog.hide();
                }
              },
              // zIndex: 12, // better to let syncfusion handle zIndex to fix SER-3834
              content: div,
            });
          }

          // The fieldViewModel.value is not initially loaded so we force it so that the callback above has the data it needs.
          // We must ensure that we don't load if it has already been done once otherwise it will retrieve the original value
          // rather than the current state on the form. Use of the quick entry modal to remove all entries is otherwise
          // indistinguishable from a pre-loaded state.
          //
          // We don't load if a new record as system templates with related lists on them look like new records, and there is
          // no real record there.
          if (self.koFieldRetainingObservables.koFieldViewModel.value().length === 0 &&
            self.koFieldRetainingObservables.koFieldViewModel.initialDataLoad === false &&
            !self.koFieldRecordIsNewRecord) {
            self.koFieldRetainingObservables.koFieldViewModel.loadData(loadDataCallback);
          } else {
            loadDataCallback();
          }

        });

        quickEntryButton.appendChild(document.createTextNode(this.i18nService.getMessage("core.relatedlist.addremove.relation")));

        this.quickEntryButton = quickEntryButton;
      }

      if (UtilService.defaultBoolean(this.koFieldRendererProperties[MagicRendererProperties.ENABLE_QUICK_ENTRY_BUTTON], this.koFieldViewModel.enableQuickEntryButton) &&
        !this.koFieldViewModeIsReadOnly &&
        (this.koFieldFormDisplayType === FormDisplayType.FORM)) {
        !pagerRangeMsg.parentElement.contains(this.quickEntryButton) && pagerRangeMsg.parentElement.insertBefore(this.quickEntryButton, pagerRangeMsg);
      } else {
        pagerRangeMsg.parentElement.contains(this.quickEntryButton) && pagerRangeMsg.parentElement.removeChild(this.quickEntryButton);
      }

      if (UtilService.defaultBoolean(this.koFieldRendererProperties[MagicRendererProperties.ENABLE_CREATE_BUTTON], this.koFieldViewModel.enableCreateButton) &&
        this.koFieldViewModel.canCreateTarget && // Initial value when the list is created
        this.koFieldRetainingObservables.koFieldViewModel.canCreateTarget && // Value that might be set by a UI event
          (this.koFieldFormDisplayType === FormDisplayType.FORM)) {
        if (!this.koFieldRecordIsNewRecord) {
          !pagerRangeMsg.parentElement.contains(this.createButton) && pagerRangeMsg.parentElement.insertBefore(this.createButton, pagerRangeMsg);
        }
      } else {
        pagerRangeMsg.parentElement.contains(this.createButton) && pagerRangeMsg.parentElement.removeChild(this.createButton);
      }

      // PF: enable display of count on related list to see if we want it
      // pagerObj.enablePagerMessage = false;
    }
  }

  private onCreatedPagerExternalMessageSupport(e: Object) {
    // The external message is the number of selected items, including on other pages
    this.listGrid.pagerModule.pagerObj.enableExternalMessage = true;
    this.listGrid.pagerModule.pagerObj.externalMessage = this.NBSP;
  }

  // Update grid from popup
  private updateDataFromEventUpdate(tableName: string, recordId: string, eventName: string, koTableRecord: any, relationshipData: any) {
    this.refresh();
  }

  private relatedListUpdateCallbackFunction(tableName: string, id: string, eventName: string, data: any, relationshipData: any) {
    if (relationshipData?.relationName && (this.type == TableGridType.RelatedList || this.type == TableGridType.CustomRelatedList)) {
      const field = this.koFieldRetainingObservables.koFieldRecord[relationshipData.relationName];
      this.koFieldRetainingObservables.koFieldRootViewModel.getEventExecutor()?.onChange?.apply(field, [this.koFieldRetainingObservables.koFieldRootViewModel._properties.isLoading()]);
    }
  }

  // List customisation support

  private async loadApplicationProperty(key: string, def = null) {
    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Application")
      .method("getApplicationProperty")
      .data({
        key: key
      })
      .execute();
    return response && response.data ? response.data : def;
  }

  private async saveApplicationProperty(key: string, value: string) {
   return await this.requestService.createRequest()
      .controller("servicely.core.Application")
      .method("setApplicationProperty")
      .data({
        key: key,
        value: value
      })
      .execute();
  }

  private async deleteApplicationProperty(key: string) {
    return await this.requestService.createRequest()
      .controller("servicely.core.Application")
      .method("deleteApplicationProperty")
      .data({
        key: key
      })
      .execute();
  }

  private loadUserProperty(key: string, def: string = null) {
    const response = this.userService.getUserProfileProperty(key, null);
    return response ? response : def;
  }

  private loadIntegerUserProperty(key: string, def: number = null) {
    const response = this.userService.getIntegerUserProfileProperty(key, null);
    return response ? response : def;
  }

  private async saveUserProperty(key: string, value: string) {
    return await  this.userService.updateUserProfileSetting(key, value);
  }

  private async deleteUserProperty(key: string) {
    return await this.userService.deleteUserProfileSetting(key);
  }

  // Custom list sorting support

  private getCustomListSortingKey(def: boolean = false) {
    return `${def ? 'default' : 'user'}.list.sorting.${this.listTableName}.${this.listAspect || 'none'}`;
  }

  private async loadCustomListSorting(def?: boolean) {
    const customListSortingKey = this.getCustomListSortingKey(def);
    if (def === undefined) {
      await this.loadCustomListSorting(false);
      if (!this.listCustomSorting) {
        await this.loadCustomListSorting(true);
      }
    } else if (def) {
      this.listCustomSorting = await this.loadApplicationProperty(customListSortingKey, "");
      this.listCustomSortingButtons = this.listDefaultCustomSortingButtons;
      this.listCustomSortingTitle = $translateKey("ng.component.table-grid.select-default-sorting-columns");
      this.listCustomSortingUser = false;
    } else {
      this.listCustomSorting = this.loadUserProperty(customListSortingKey, "");
      this.listCustomSortingButtons = this.listUserCustomSortingButtons;
      this.listCustomSortingTitle = $translateKey("ng.component.table-grid.select-sorting-columns");
      this.listCustomSortingUser = true;
    }
  }

  private async saveCustomListSorting(def: boolean) {
    const customListSortingKey = this.getCustomListSortingKey(def);
    const property = this.listCustomSorting ? this.listCustomSorting : "";
    if (def) {
      await this.saveApplicationProperty(customListSortingKey, property)
    } else {
      await this.saveUserProperty(customListSortingKey, property);
    }
  }

  private async deleteCustomListSorting(def : boolean) {
    const customListSortingKey = this.getCustomListSortingKey(def);
    if (def) {
      await this.deleteApplicationProperty(customListSortingKey);
    } else {
      await this.deleteUserProperty(customListSortingKey);
    }

    this.loadInitialData();
  }

  // Custom list column widths support

  private getCustomListColumnWidthsKey() {
    return `user.list.widths.${this.listTableName}.${this.listAspect || 'none'}`;
  }

  private async loadCustomListColumnWidths() {
    const customListDefinitionKey = this.getCustomListColumnWidthsKey();
    const customListDefinition = this.loadUserProperty(customListDefinitionKey, null);
    this.listCustomColumnWidths = customListDefinition ? JSON.parse(customListDefinition) : null;
  }

  private async saveCustomListColumnWidths() {
    const customListDefinitionKey = this.getCustomListColumnWidthsKey();
    const columnWidths = (this.listGrid.columns as Column[]).reduce((acc, curr) => { acc[curr.field] = curr.width; return acc}, {});
    const property = JSON.stringify(columnWidths);
    await this.saveUserProperty(customListDefinitionKey, property);
    this.listCustomColumnWidths = columnWidths;
  }

  private async deleteCustomListColumnWidths() {
    const customListDefinitionKey = this.getCustomListColumnWidthsKey();
    await this.deleteUserProperty(customListDefinitionKey);
    this.listCustomColumnWidths = null;
    this.loadInitialData();
  }

  // Custom view definition support

  private getCustomListDefinitionKey() {
    return `user.list.definition.${this.listTableName}.${this.listAspect || 'none'}`;
  }

  private async loadCustomListDefinition() {
    const customListDefinitionKey = this.getCustomListDefinitionKey();
    const customListDefinition = this.loadUserProperty(customListDefinitionKey, null);
    this.listCustomFields = customListDefinition ? JSON.parse(customListDefinition) : [];
  }

  private async saveCustomListDefinition() {
      const customListDefinitionKey = this.getCustomListDefinitionKey();
      const property = JSON.stringify(this.listCustomFields ? this.listCustomFields : []);
      await this.saveUserProperty(customListDefinitionKey, property);
  }

  private async deleteCustomListDefinition() {
    const customListDefinitionKey = this.getCustomListDefinitionKey();
    await this.deleteUserProperty(customListDefinitionKey);
    this.loadInitialData();
  }

  private listCustomFieldsFilterShowDialog() {
    this.listCustomFieldsButtons = this.listCustomFieldsFilterButtons;
    this.listCustomFieldsTitle = this.listCustomFieldsFilterTitle;
    this.listCustomFieldsContext = {};
    this.listCustomFieldsShowDialog();
  }

  private listCustomFieldsExportShowDialog(type: string) {
    this.listCustomFieldsButtons = this.listCustomFieldsExportButtons;
    this.listCustomFieldsTitle = this.listCustomFieldsExportTitle;
    this.listCustomFieldsContext = {type: type};
    this.listCustomFieldsShowDialog();
  }

  private listCustomFieldsShowDialog() {

    const allFieldNames = this.listModel.tableDefinition._fields
      .filter((field) => {return !['REFERENCE', 'GENERIC_REFERENCE', 'GENERIC_REFERENCE_V2'].includes(field._fieldType)})
      .map((field) => field._fieldName == 'id' ? 'Id' : field._fieldName);

    const allRelationshipNames = this.listModel.tableDefinition._relationships
      .filter((field) => {return !['MANY_TO_MANY', 'MANY_TO_ONE'].includes(field._relationshipType)})
      .map((field) => field._relationshipName);

    let candidateCustomFields = this.listCustomFields;
    if (candidateCustomFields.length === 0) {
      // Use the original view definition to give the fields for the dialog
      candidateCustomFields = this.listModel.viewDefinition.containers[0].elements.map((field) => field.fieldName);
    }

    this.listCustomFieldsAvailableFields = [...allFieldNames, ...allRelationshipNames]
      .filter(x => !candidateCustomFields.includes(x))
      .map((field) => {
      const iconCss = allRelationshipNames.includes(field) ? "mi mi_fork_right" : "mi mi_edit";
      return {text: field, iconCss: iconCss};
    });

    this.listCustomFieldsIncludedFields = candidateCustomFields
      .map((field) => {
      const iconCss = allRelationshipNames.includes(field) ? "mi mi_fork_right" : "mi mi_edit";
      return {text: field, iconCss: iconCss};
    });

    this.ejDialog.show();
  }

  public listCustomFieldsFilterHideDialog(save: boolean) {
    this.ejDialog.hide();

    if (save) {
      this.listCustomFields = (this.ejIncludedFields.getDataList() as any[]).map((data) => data.text) as string[];
      this.saveCustomListDefinition().then(() => {
        this.loadInitialData();
      });
    }
  }

  public listCustomFieldsExportHideDialog(save: boolean) {
    this.ejDialog.hide();

    if (save) {
      const fields = (this.ejIncludedFields.getDataList() as any[]).map((data) => data.text) as string[];
      const type = this.listCustomFieldsContext.type;

      // Use the list and fields as is if there has been no alteration.
      const fieldsToUse = fastDeepEqual(fields, this.listCustomFields) ? [] : fields;

      this.listExportSelect(type, fieldsToUse);
    }
  }

  private async listCustomSortingShowDialog(def: boolean) {
    const allFieldNames = this.listModel.tableDefinition._fields
      .filter((field) => {return !['REFERENCE', 'GENERIC_REFERENCE', 'GENERIC_REFERENCE_V2'].includes(field._fieldType)})
      .map((field) => field._fieldName);

    const allRelationshipNames = this.listModel.tableDefinition._relationships
      .filter((field) => {return !['MANY_TO_MANY', 'MANY_TO_ONE'].includes(field._relationshipType)})
      .map((field) => field._relationshipName);

    let candidateCustomSorting = [];
    let candidateCustomFields = [];

    await this.loadCustomListSorting(def);

    if (this.listCustomSorting && this.listCustomSorting !== "") {
      candidateCustomSorting = this.listDataService.convertToDataGridSorted(this.listCustomSorting);
      candidateCustomFields = candidateCustomSorting.map((field) => field.name);
    }

    this.listCustomSortingAvailableFields = [...allFieldNames, ...allRelationshipNames]
      .filter(x => !candidateCustomFields.includes(x))
      .map((field) => {
        return {text: field, direction: 'descending', iconCss: "mi mi_arrow_drop_down"};
      });

    this.listCustomSortingIncludedFields = candidateCustomSorting
      .map((field) => {
        const iconCss = field.direction === 'descending' ? "mi mi_arrow_drop_down" : "mi mi_arrow_drop_up";
        return {text: field.name, direction: field.direction, iconCss: iconCss};
      });

    this.ejSortingDialog.show();
  }

  public listCustomSortingHideDialog(save: boolean, def: boolean) {
    this.ejSortingDialog.hide();

    if (save) {
      const sortFields = (this.ejIncludedSortingFields.getDataList() as any[]).map((field) => {
        return {
          name: field.text,
          direction: field.direction
        };
      });
      this.listCustomSorting = this.listDataService.convertToStorageSorted(sortFields);
      this.saveCustomListSorting(def).then(() => {
        this.loadInitialData();
      });

    }
  }

  private async storeCustomColumnWidths() {
    const columns = (this.listGrid.columns as Column[]).reduce((acc, curr) => { acc[curr.field] = curr.width; return acc}, {});

  }

  private async deleteListColumnWidths() {
    const customListDefinitionKey = this.getCustomListColumnWidthsKey();
    await this.deleteUserProperty(customListDefinitionKey);
    this.listCustomColumnWidths = null;
    this.loadInitialData();
  }

  customSortingOnChange(e: ListBoxChangeEventArgs) {
    if (e.items.length > 0) {
      e.items.forEach((item, idx) => {
        if ((item as any).direction === 'descending') {
          (item as any).direction = 'ascending';
          (item as any).iconCss = "mi mi_arrow_drop_up";
          (e.elements[idx].firstChild as any).classList.remove('mi_arrow_drop_down');
          (e.elements[idx].firstChild as any).classList.add('mi_arrow_drop_up');
        } else {
          (item as any).direction = 'descending';
          (item as any).iconCss = '"mi mi_arrow_drop_down';
          (e.elements[idx].firstChild as any).classList.remove('mi_arrow_drop_up');
          (e.elements[idx].firstChild as any).classList.add('mi_arrow_drop_down');
        }
      });
      this.ejAvailableSortingFields.selectAll(false);
      this.ejIncludedSortingFields.selectAll(false);
    }
  }

  // Saved filters support

  private getSavedFiltersListDefinitionKey() {
    return `user.list.savedfilters.${this.listTableName}.${this.listAspect || 'none'}`;
  }

  @ViewChild('savedFiltersFilterName', {static: false}) savedFiltersFilterName: HTMLInputElement;

  @ViewChild('savedFiltersListView', {static: false}) savedFiltersListView: ListViewComponent;

  // List of saved filters
  public savedFiltersListViewData: any[] = [{
      text: $translate("ng.component.table-grid.saved-filters.new")`New`,
      id: 'new'
    }];

  // Content menu items
  public savedFiltersContextMenuItems: MenuItemModel[] = [
    {
      text: $translate("ng.component.table-grid.saved-filters.edit")`Edit`,
      id: 'edit',
      iconCss: "mi mi_edit"
    },
    {
      text: $translate("ng.component.table-grid.saved-filters.delete")`Delete`,
      id: 'delete',
      iconCss: "mi mi_delete"
    },
  ];

  savedFiltersItemId: string = null;
  savedFiltersDialog: any = null;
  savedFiltersEdit: boolean = false;

  savedFiltersListViewOnSelect(e: SelectEventArgs) {
    // Selecting an item from the filter list
    if ((e.data as any).id === 'new') {
      // display the query builder and the edit buttons
      (this.savedFiltersFilterName as any).nativeElement.value = "";
      this.listShowQueryBuilder = true;
      this.listQueryBuilderViewModel && this.listQueryBuilderViewModel.isVisible(true);
      this.savedFiltersEdit = true;
    } else {
      // set current filter
      const filter = e.data.filter ? JSON.parse(e.data.filter as string) : null;
      if (filter) {
        this.listQueryBuilderViewModel && this.listQueryBuilderViewModel.setAndApplyInitialQuery(filter);
      }
    }
    (this.savedFiltersListView.getSelectedItems().item as HTMLElement).classList.remove('e-active');
  }

  savedFiltersContextMenuOnSelect(e: SelectEventArgs, id: string) {
    // Select an item from the context menu and perform the action
    // this.savedFiltersItemId is the id of the filter chosen (look in savedFiltersListViewData) and
    // e.item.properties.id is the action to perform
    this.savedFiltersItemId = id;
    if ((e.item as any).properties.id === "delete") {
      this.savedFiltersDialog = DialogUtility.confirm({
        title: $translate("ng.component.table-grid.delete-saved-filter.title")`Delete saved filter`,
        content: $translate("ng.component.table-grid.delete-saved-filter.message")`Are you sure you want to delete the saved filter?`,
        isModal: true,
        isDraggable: false,
        showCloseIcon: true,
        closeOnEscape: true,
        okButton: {  text: $translateKey("common.button.ok"), click: () => { this.deleteSavedFilter(this.savedFiltersItemId); } },
        cancelButton: {  text: $translateKey("common.button.cancel"), click: () => {
          this.savedFiltersDialog.hide();
          } },
      });
    } else if ((e.item as any).properties.id === "edit") {
      const filter = this.savedFiltersListViewData.find((f) => f.id === this.savedFiltersItemId);
      if (filter) {
        const filterJson = filter.filter ? JSON.parse(filter.filter): null;
        this.listShowQueryBuilder = true;
        this.listQueryBuilderViewModel && this.listQueryBuilderViewModel.isVisible(true);
        (this.savedFiltersFilterName as any).nativeElement.value = filter.text;
        if (filterJson) {
          this.listQueryBuilderViewModel.setAndApplyInitialQuery(filterJson);
        }
        this.savedFiltersEdit = true;
      }
    }
  }

  deleteSavedFilter(id: string) {
    const preservedFilters = this.savedFiltersListViewData.filter((f) => f.id != id && f.id != 'new');
    const managedFilters = this.savedFiltersListViewData.filter((f) => f.id != id);
    const filtersString = JSON.stringify(preservedFilters);
    this.savedFiltersListViewData = cloneDeep(managedFilters);

    this.saveUserProperty(this.getSavedFiltersListDefinitionKey(), filtersString);

    this.savedFiltersDialog.hide();
  }

  savedFiltersEditFilter(e: any, id: string) {
    const filter = this.savedFiltersListViewData.find((f) => f.id === id);
    if (filter) {
      this.listShowQueryBuilder = true;
      this.listQueryBuilderViewModel && this.listQueryBuilderViewModel.isVisible(true);
      (this.savedFiltersFilterName as any).nativeElement.value = filter.text;
      this.listQueryBuilderViewModel.setAndApplyInitialQuery(filter.filter);
      this.savedFiltersEdit = true;
    }
  }

  savedFiltersDeleteFilter(e: any, id: string) {
    this.savedFiltersItemId = id;
    this.savedFiltersDialog = DialogUtility.confirm({
      title: "Delete saved filter",
      content: "Are you sure you want to delete the saved filter?",
      isModal: true,
      isDraggable: false,
      showCloseIcon: true,
      closeOnEscape: true,
      okButton: {  text: 'OK', click: () => { this.deleteSavedFilter(this.savedFiltersItemId); } },
      cancelButton: {  text: 'Cancel', click: () => {
          this.savedFiltersDialog.hide();
        } },
    });
  }

  // Column filters

  allowFiltering = false;

  showColumnFilters = false;

  filterSettings: FilterSettingsModel = {
    type: 'FilterBar',
    mode: 'Immediate',
    showFilterBarStatus: false,
    ignoreAccent: true,
    immediateModeDelay: 500,
    enableCaseSensitivity: false,
  };

  private GRID_OPERATOR_TO_CONDITION_OPERATOR = {
    startswith: 'startswith',
    endswith: 'endswith',
    contains: 'like',
    equal: '=',
    notequal: '!=',
    greaterthan: '>',
    greaterthanorequal: '>=',
    lessthan: '<',
    lessthanorequal: '<=',
  };

  // Grouping

  allowGrouping = false;

  groupSettings: GroupSettingsModel = {
    showDropArea: false,
    showToggleButton: true,
    showGroupedColumn: true,
  };

  // Aggregations

  listAggregates: AggregateRowModel[] = [];

  listDataAggregates: ListDataAggregateModel[] = [];

  private formatCustomAggregate(data: Object[] | Object, column: AggregateColumnModel): any {
    const fieldName = column.field;
    const agg = this.listModel?.aggregates?.filter((x: ListDataAggregateModel) => x.field === fieldName).sort((a,b) => a.type.localeCompare(b.type)) || [];
    const result = agg.reduce((acc: string, curr: ListDataAggregateModel) => {
      const type = curr.type == 'Average' ? 'Avg' : curr.type;
      const col: Column = this.listGrid.getColumnByField(fieldName);
      const targetedRow = (data as any[]).length > 0 ? data[0] : null;
      // Take the dataFn from one of the cells
      const d = Object.fromEntries([
        [fieldName, curr.value],
        ["_dataFn", targetedRow?._dataFn]
      ]);
      // Start with the raw value
      let value = curr.value;
      if (value != null) {
        if (type != "Count") {
          // Format according to the cell
          value = targetedRow ? this.getFormattedCellValue(col, d) : "";
          // Special case for DURATION: getFormattedCellValue uses the displayValue of the targetedRow
          // and we want the displayValue of curr.value instead.
          if (targetedRow && targetedRow._dataFn()[fieldName].fieldType === 'DURATION') {
            value = (window as any).Duration.parseDurationTime(curr.value, 'days');
          }
        }
        return acc + ` ${type}: ${value}`;
      } else {
        return acc;
      }
    }, "");

    return result;
  }

  private clearAggregation() {
    this.listAggregates = [];
    this.listDataAggregates = [];
  }

  private addAggregation(field: string, type: AggregateType) {
    const exists = this.listDataAggregates.filter((c) => c.field == field && c.type == type).length > 0;

    if (!exists) {
      this.listDataAggregates.push({
        field: field,
        type: type,
      });
      this.generateDataAggregation();
      this.utilService.tickThen(() => {
        this.refresh();
      });
    }
  }

  private removeAggregation(field: string, type: string) {
    this.listDataAggregates = this.listDataAggregates.filter((a) => !(a.field == field && a.type == type));
    this.generateDataAggregation();
    this.utilService.tickThen(() => {
      this.refresh();
    });
  }

  private hasAggregation(field: string, type: string) {
      return this.listDataAggregates.filter((c) => c.field == field && c.type == type)?.length > 0;
  }

  private generateDataAggregation() {
    const fields = this.listDataAggregates
      .map(a => a.field)
      .filter((value, index, array) => array.indexOf(value) === index);

    const columns = fields.map((field) => {
      return {
        field: field,
        type: 'Custom',
        columnName: field,
        footerTemplate: '${Custom}',
        customAggregate: this.formatCustomAggregate.bind(this),
      };
    });
    this.listAggregates = [{
      columns: columns,
    }];
  }

  // Column resize support

  // We explicitly size the columns using modifications of SF grid internal code to make this data grid
  // behave more like the old one with regards to column sizing.
  // If isDataBound is false then we are reacting to the resize of the browser rather than
  // going to a new page. If just resizing the browser then the original width of the
  // content should still be valid and will not need to be recalculated.

  private resizeColumns(isDataBound: boolean) {

    const resizeModule = this.listGrid?.resizeModule as any;

    if (!resizeModule?.widthService) {
      return;
    }

    if (this.listGrid.getColumns().length === 0) {
      return;
    }

    const primaryKeyColumn = this.listGrid.getColumnByField("primaryKey");
    const selectionColumn = this.listGrid.getColumnByField("selection");
    const previewColumn = this.listGrid.getColumnByField("preview");
    const adjustedColumns = this.listGrid.getColumns().filter((c) => !['primaryKey', 'selection', 'preview'].includes(c.field));

    // Get total table width
    // WARNING: this is using an internal SF data structure and so needs to be checked with every SF version upgrade.
    const contentTable = (this.listGrid.contentModule as any).contentTable;

    if (!contentTable) {
      console.debug("TableGridComponent.resizeColumns - contentTable is not defined");
      return;
    }

    const contentTableParent = contentTable.parentElement;
    // TODO: Why does this have to be about 10? Some kind of padding/margin thing?
    const tableWidth = contentTableParent.offsetWidth - 10;

    // Calculate original widths with an upper limit
    const originalWidths: any = {};
    if (isDataBound) {
      adjustedColumns.forEach((c) => {
        // TODO: Why does this have to be about 1 to get rid of the ellipsis? Some kind of padding/margin thing?
        const contentWidth = this.getColumnWidth(c.field) + 1;
        (c as any)._originalWidth = contentWidth;
        originalWidths[c.field] = contentWidth;
      });
    } else {
      adjustedColumns.forEach((c) => {
        originalWidths[c.field] = (c as any)._originalWidth;
      });
    }

    // Limit the width of any column to a maximum initially and add up the total
    let totalWidth = this.SELECTION_COLUMN_WIDTH + this.PREVIEW_COLUMN_WIDTH;
    Object.keys(originalWidths).forEach((k) => {
      const contentWidth = originalWidths[k];
      const limitedWidth = Math.min(contentWidth, this.MAX_COLUMN_WIDTH);
      totalWidth += limitedWidth;
      originalWidths[k] = limitedWidth;
    });

    // Calculate the difference between the total width and the table width and divide between the columns
    let delta = (totalWidth < tableWidth) ? Math.round((tableWidth - totalWidth) / adjustedColumns.length) : 0;

    // Apply the delta to each column
    Object.keys(originalWidths).forEach((k) => {
      originalWidths[k] = originalWidths[k] + delta;
    });

    // Set the width on each column - use a custom width if available.
    adjustedColumns.forEach((c) => {
      if (this.listCustomColumnWidths && this.listCustomColumnWidths[c.field]) {
        c.width = formatUnit(this.listCustomColumnWidths[c.field]);
      } else {
        c.width = formatUnit(originalWidths[c.field]);
      }
    });

    // Set the widths in the DOM
    primaryKeyColumn && resizeModule.widthService.setColumnWidth(primaryKeyColumn);
    selectionColumn && resizeModule.widthService.setColumnWidth(selectionColumn);
    previewColumn && resizeModule.widthService.setColumnWidth(previewColumn);
    adjustedColumns.forEach((c) => {
      resizeModule.widthService.setColumnWidth(c);
    });
  }

  // Taken from SF and modified to just return the width.
  private getColumnWidth(fName: string): number {
    const gObj = this.listGrid;
    const fieldName = fName;
    const columnIndex = gObj.getColumnIndexByField(fieldName);
    const column = gObj.getColumns()[columnIndex];
    if (columnIndex > -1 && !isNullOrUndefined(column) && column.visible === true) {
      return this._getColumnWidth(fieldName, columnIndex);
    }
  }

  // Taken from SF and modified to just return the width and to remove some style attributes that
  // were affecting the width calculations.
  private _getColumnWidth(fName: string, index: number, id?: string): number {

    const resizeModule = this.listGrid.resizeModule;
    const widthService = (resizeModule as any).widthService;

    const gObj = this.listGrid;
    let tWidth: number = 0;
    let headerTable: Element;
    let contentTable: Element;
    let footerTable: Element;
    const headerDivTag: string = 'e-gridheader';
    const contentDivTag: string =  'e-gridcontent';
    const footerDivTag: string = 'e-gridfooter';
    let indentWidth: number = 0;
    const uid: string = id ? id : gObj.getUidByColumnField(fName);
    const columnIndex: number = gObj.getNormalizedColumnIndex(uid);
    let headerTextClone: Element;
    let contentTextClone: NodeListOf<Element>;
    let footerTextClone: NodeListOf<Element>;
    const columnIndexByField: number = gObj.getColumnIndexByField(fName);
    const left: number = gObj.getFrozenColumns() || gObj.getFrozenLeftColumnsCount();
    const movable: number = gObj.getMovableColumnsCount();
    if (!isNullOrUndefined(gObj.getFooterContent())) {
      footerTable = gObj.getFooterContentTable();
    }
    if (gObj.isFrozenGrid()) {
      const col: Column = gObj.getColumnByField(fName);
      if (col.getFreezeTableName() === 'frozen-left') {
        headerTable = gObj.getHeaderTable();
        contentTable = gObj.getContentTable();
        headerTextClone = (<HTMLElement>headerTable.querySelector('[e-mappinguid="' + uid + '"]').parentElement.cloneNode(true));
        contentTextClone = contentTable.querySelectorAll(`td:nth-child(${columnIndex + 1})`);
        if (footerTable) {
          footerTextClone = footerTable.querySelectorAll(`td:nth-child(${columnIndex + 1})`);
        }
      } else if (col.getFreezeTableName() === 'movable') {
        headerTable = gObj.getHeaderContent().querySelector('.' + 'e-movableheader').children[0];
        contentTable = gObj.getContent().querySelector('.' + 'e-movablecontent').children[0];
        headerTextClone = (<HTMLElement>headerTable.querySelector('[e-mappinguid="' + uid + '"]').parentElement.cloneNode(true));
        contentTextClone = contentTable.querySelectorAll(`td:nth-child(${(columnIndex - left) + 1})`);
        if (footerTable) {
          footerTable = gObj.getFooterContent().querySelector('.e-movablefootercontent').children[0];
          footerTextClone = footerTable.querySelectorAll(`td:nth-child(${(columnIndex - left) + 1})`);
        }
      } else if (col.getFreezeTableName() === 'frozen-right') {
        headerTable = gObj.getHeaderContent().querySelector('.e-frozen-right-header').children[0];
        contentTable = gObj.getContent().querySelector('.e-frozen-right-content').children[0];
        headerTextClone = (<HTMLElement>headerTable.querySelector('[e-mappinguid="' + uid + '"]').parentElement.cloneNode(true));
        contentTextClone = contentTable.querySelectorAll(`td:nth-child(${(columnIndex - (left + movable)) + 1})`);
        if (footerTable) {
          footerTable = gObj.getFooterContent().querySelector('.e-movablefootercontent').children[0];
          footerTextClone = footerTable.querySelectorAll(`td:nth-child(${(columnIndex - (left + movable)) + 1})`);
        }
      }
    } else {
      headerTable = gObj.getHeaderTable();
      contentTable = gObj.getContentTable();
      // Modified for PDF export
      headerTextClone = (<HTMLElement>headerTable.querySelector('[e-mappinguid="' + uid + '"]')?.parentElement?.cloneNode(true));
      contentTextClone = contentTable.querySelectorAll(`td:nth-child(${columnIndex + 1}):not(.e-groupcaption)`);
      if (footerTable) {
        footerTextClone = footerTable.querySelectorAll(`td:nth-child(${columnIndex + 1}):not(.e-groupcaption)`);
      }
    }
    const indentWidthClone: NodeListOf<Element> = [].slice.call(headerTable.querySelector('tr').getElementsByClassName('e-grouptopleftcell'));
    if (indentWidthClone.length > 0) {
      for (let i: number = 0; i < indentWidthClone.length; i++) {
        indentWidth += (<HTMLElement>indentWidthClone[i]).offsetWidth;
      }
    }
    const detailsElement: HTMLElement = <HTMLElement>contentTable.querySelector('.e-detailrowcollapse') ||
      <HTMLElement>contentTable.querySelector('.e-detailrowexpand');
    if ((gObj.detailTemplate || gObj.childGrid) && detailsElement) {
      indentWidth += detailsElement.offsetWidth;
    }
    // Modified for PDF export
    const headerText: Element[] = headerTextClone ? [headerTextClone] : [];
    const contentText: Element[] = [];
    const footerText: Element[] = [];
    if (footerTable) {
      for (let i: number = 0; i < footerTextClone.length; i++) {
        footerText[i] = footerTextClone[i].cloneNode(true) as Element;
      }
    }
    for (let i: number = 0; i < contentTextClone.length; i++) {
      contentText[i] = contentTextClone[i].cloneNode(true) as Element;
    }

    // NOTE: PF: remove min-width style as it is affecting the width calculation.
    headerText.forEach((e) => {
      (e as HTMLElement).style.minWidth = null;
    });
    contentText.forEach((e) => {
      (e as HTMLElement).style.minWidth = null;
    });
    footerText.forEach((e) => {
      (e as HTMLElement).style.minWidth = null;
    });

    const wHeader: number = (resizeModule as any).createTable(headerTable, headerText, headerDivTag);
    const wContent: number = (resizeModule as any).createTable(contentTable, contentText, contentDivTag);
    let wFooter: number = null;
    if (footerText.length) {
      wFooter = (resizeModule as any).createTable(footerTable, footerText, footerDivTag);
    }

    // NOTE: PF: early return.
    // Rest of code actually sets the column width and the table width, which we don't want at this point.
    // We do it in this.resizeColumns()
    return Math.max(wHeader, wContent, wFooter);

    // const columnbyindex: Column = gObj.getColumns()[columnIndexByField];
    // const width: string = columnbyindex.width = formatUnit(Math.max(wHeader, wContent, wFooter));
    // const colMaxWidth: number =  columnbyindex.maxWidth && parseFloat(columnbyindex.maxWidth.toString());
    // if (parseInt(width, 10) > colMaxWidth) {
    //   columnbyindex.width = colMaxWidth;
    // }
    // widthService.setColumnWidth(gObj.getColumns()[columnIndexByField] as Column);
    // const result: boolean = gObj.getColumns().some((x: Column) => x.width === null
    //   || x.width === undefined || (x.width as string).length <= 0);
    // if (result === false) {
    //   const element: Column[] = (gObj.getColumns() as Column[]);
    //   for (let i: number = 0; i < element.length; i++) {
    //     if (element[i].visible) {
    //       tWidth = tWidth + parseFloat(element[i].width as string);
    //     }
    //   }
    // }
    // let calcTableWidth: string | number = tWidth + indentWidth;
    // if (tWidth > 0 && !gObj.isFrozenGrid()) {
    //   if (gObj.detailTemplate || gObj.childGrid) {
    //     widthService.setColumnWidth(new Column({ width: '30px' }));
    //   }
    //   if (gObj.resizeSettings.mode === 'Auto') {
    //     calcTableWidth = '100%';
    //   }
    //   (<HTMLTableElement>headerTable).style.width = formatUnit(calcTableWidth);
    //   (<HTMLTableElement>contentTable).style.width = formatUnit(calcTableWidth);
    //   if (!isNullOrUndefined(footerTable)) {
    //     (<HTMLTableElement>footerTable).style.width = formatUnit(calcTableWidth);
    //   }
    // }
    // if (gObj.isFrozenGrid()) {
    //   widthService.refreshFrozenScrollbar();
    // }
    // const tableWidth: number = (headerTable as HTMLElement).offsetWidth;
    // const contentwidth: number = (gObj.getContent().scrollWidth);
    // if (contentwidth > tableWidth) {
    //   headerTable.classList.add('e-tableborder');
    //   contentTable.classList.add('e-tableborder');
    // } else {
    //   headerTable.classList.remove('e-tableborder');
    //   contentTable.classList.remove('e-tableborder');
    // }
    // if (!isNullOrUndefined(footerTable)) {
    //   footerTable.classList.add('e-tableborder');
    // }
  }
}

// Alternative implementation to support formatted grouped results.
// class ListDataAdaptor extends CustomDataAdaptor {
//   result: DataResult;
//   comp: TableGridComponent;
//   constructor(result: DataResult, comp: TableGridComponent) {
//     super();
//     this.result = result;
//     this.comp = comp;
//     this.options.getData = (options: {data: string, onSuccess: Function, onFailure: Function}) => {
//       const state = JSON.parse(options.data);
//
//       const patchedState = this.comp.listDataService.patchDataGridSortOrder(state);
//
//       // These are from the grid. If the grid has no sorting defined then we use the custom sorting if it is defined.
//       this.comp.listCurrentSorted = patchedState.sorted ? cloneDeep(patchedState.sorted) : this.comp.listDataService.convertToDataGridSorted(this.comp.listCustomSorting);
//       this.comp.listCurrentSkip = patchedState.skip;
//       this.comp.listCurrentTake = patchedState.take;
//
//       const fakeRequest = {
//         httpRequest: {
//           getResponseHeader(name: string) {
//             if (name == 'Content-Type') {
//               return 'application/json';
//             } else {
//               return null;
//             }
//           }
//         }
//       };
//
//       this.comp.listDataService.executeReturningPromise(patchedState, this.comp.patchListDataContext(this.comp.listCurrentDataContext))
//         .then((result) => {
//           options.onSuccess(result, fakeRequest);
//         })
//         .catch((err) => {
//           options.onFailure(err, fakeRequest);
//         });
//     };
//   }
//   processQuery(dataManager: DataManager, query: Query): Object {
//     return super.processQuery(dataManager, query);
//   }
// }
